import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, useScroll, useTransform, useSpring, useInView, useMotionValue, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import AdminPage from "./AdminPage";
import ThankYouPage from "./ThankYouPage";
import { mpTrack, trackConversion } from "./lib/metaPixel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BROCHURE_URL = "/assets/cmm-brochure-2026.pdf";
const EVENT_ISO = "2026-10-01T15:30:00+05:30";

const HERO_IMG = "/assets/hero-1st-edition.jpg";
const EVENING_IMG = "/assets/evening-room.jpg";
const GALLERY = [
  { src: "/assets/gallery-01-awards-stage.jpg", label: "Awards Stage" },
  { src: "/assets/gallery-02-cocktail-hour.jpg", label: "Cocktail Hour" },
  { src: "/assets/gallery-03-panel.jpg", label: "Panel In Action" },
  { src: "/assets/gallery-04-networking.jpg", label: "Networking" },
  { src: "/assets/gallery-05-keynote.jpg", label: "Keynote" },
  { src: "/assets/gallery-06-trophy-moment.jpg", label: "Trophy Moment" },
  { src: "/assets/gallery-07-conversations.jpg", label: "Conversations" },
  { src: "/assets/gallery-08-gala-dinner.jpg", label: "Gala Dinner" },
  { src: "/assets/gallery-09-standing-ovation.jpg", label: "Standing Ovation" },
  { src: "/assets/gallery-10-backstage.jpg", label: "Backstage" },
  { src: "/assets/gallery-11-the-room.jpg", label: "The Room" },
  { src: "/assets/gallery-12-toasts.jpg", label: "Toasts & Talk" },
];

const AWARDS = {
  A: { title: "Agri Commodities", tone: "cyan", items: ["Agri Commodity Leader of the Year","Excellence in Edible Oil Trade","Grain & Feed Leadership","Sugar & Bioenergy Excellence","Emerging Agri Trade Enterprise","Sustainability Leadership in Agri"] },
  B: { title: "Non-Agri Commodities", tone: "gold", items: ["Commodity Market Leader of the Year","Precious Metals & Bullion Excellence","Metals & Mining Leadership","Energy & Industrial Commodity Excellence","Commodity Innovation & Technology"] },
  C: { title: "Special Recognition", tone: "grad", items: ["Lifetime Contribution to Commodity Trade","Young Commodity Leader","Women Leadership in Commodities","Global Trade Excellence","Supply Chain & Logistics Leadership","Excellence in Risk & Market Intelligence"] },
};
const ALL_CATEGORIES = [...AWARDS.A.items, ...AWARDS.B.items, ...AWARDS.C.items];

const AGENDA = [
  { time: "2:45 PM", end: "onwards", title: "Registration & Networking", note: "Arrive early — the quiet half hour is where the good conversations start.", kind: "break" },
  { time: "3:00 PM", end: "3:30 PM", title: "Opening Remarks & Welcome Address · Future-Focused Keynote", theme: "Commodities 2027: The New Cycle of Volatility, Opportunity & Global Realignment", kind: "keynote", panel: ["Atul Chaturvedi", "Mrugank Paranjape", "Nagaraj Meda", "Deepak Pareek", "Aadil Singh"] },
  { time: "3:30 PM", end: "4:15 PM", track: "Agri Commodities", title: "Business Session I — Pulses Leadership Dialogue", theme: "Pulses 2027: Global Supply, Indian Demand, Trade & Price Outlook", moderator: "Deepak Pareek", panel: ["Sumit Gupta", "Shailesh Tulsiyan", "Manav Modi", "Gaurav Jain", "Rajesh Paharia", "Somnath Chatterjee"] },
  { time: "4:15 PM", end: "5:00 PM", track: "Agri Commodities", title: "Business Session II — Pulses Leadership Dialogue", theme: "Pulses for India: Affordability, Availability & the Protein Security Challenge", moderator: "G. Chandrashekhar", panel: ["Sunil Kumar Singh", "Binod Agarwal", "Bimal Bengani", "Rahul Chauhan", "Yogesh Shivanna", "Ankush Jain"] },
  { time: "5:00 PM", end: "5:45 PM", track: "Agri & Grains Outlook", title: "Business Session III — Agri & Grains Outlook", theme: "Beyond Pulses: Grains, Feed & Agri Markets in a Changing Global Trade Order", moderator: "Atul Chaturvedi", panel: ["Sanjeev Asthana", "Sandeep Bajoria", "Dr. B.V. Mehta", "Ravi Gupta", "Rahil Sheikh", "Dilip Mohanty", "Kunal Karan Parakh", "Vijay Sardana"] },
  { time: "5:45 PM", end: "6:30 PM", track: "Energy", title: "Business Session IV — Energy", theme: "Energy Markets at the Crossroads: Navigating Crude, Gas, Geopolitics & Price Volatility", moderator: "Mrituenjay Jha", panel: ["J.K. Chaturvedi", "Dr. Sudharanjan Sahu", "Deepak Mehta", "Barleen Kaur", "Anurag Saraogi", "Hitesh Sethia", "Anuj Jain"] },
  { time: "6:30 PM", end: "7:15 PM", track: "Metals & Bullion", title: "Business Session V — Metals & Bullion", theme: "Metals & Bullion 2027: Gold, Silver & Base Metals in a New Market Cycle", moderator: "Manisha Gupta", panel: ["Mrugank Paranjape", "Dr. V. Shunmugam", "Chittaranjan Rege", "Nagaraj Meda", "Aurobinda Gayan", "Sandeep Daga", "Alok Ranjan", "Vikram Dhawan"] },
  { time: "7:15 PM", end: "7:45 PM", track: "Risk Management", title: "Closing Market Dialogue — Risk Management", theme: "Managing the Unmanageable: Risk, Volatility & Hedging Across Agri & Non-Agri Commodities", kind: "keynote", panel: ["Nagaraj Meda"] },
  { time: "7:45 PM", end: "onwards", title: "Commodity Market Movers Felicitations & Awards", note: "Followed by Awards, Cocktails, Entertainment & Dinner — the part everyone really comes for.", kind: "awards" },
];

const ROOM_TILES = ["Traders & merchants","Importers & exporters","Refiners & processors","Exchanges & brokers","Banks & financiers","Logistics & shipping","Risk & analytics firms","Policymakers & associations","Investors & funds","Agri-business leaders","Industrial commodity heads","Consultants & experts"];

const QUESTIONS = [
  { q: "How long in the commodity trade?", opts: [["Under 3 years", 0], ["3–8 years", 1], ["8–15 years", 2], ["15+ years", 2]] },
  { q: "Your role?", opts: [["Early-career / executive", 0], ["Manager / desk lead", 1], ["Director / VP / Partner", 2], ["Founder / CXO / Owner", 2]] },
  { q: "Scale of business?", opts: [["Building it", 0], ["Established regional", 1], ["National-scale", 2], ["Global / multi-market", 2]] },
  { q: "Part of the chain?", opts: [["Agri", 1], ["Metals / bullion / energy", 1], ["Logistics / finance / risk", 1], ["Tech / market intelligence", 1]] },
  { q: "Done something notable in the last 18 months?", helper: "real growth, a market-first, a sustainability win, an acquisition, an innovation", opts: [["Not really", 0], ["One solid achievement", 1], ["Several", 2], ["A genuine market-first", 2]] },
];

const NAV_LINKS = [
  ["Speakers", "#speakers"],
  ["Experience", "#experience"],
  ["Programme", "#agenda"],
  ["Honours", "#awards"],
  ["Recap", "#gallery"],
  ["Eligibility", "#eligibility"],
  ["Partner", "#partner"],
];

const FAQ_ITEMS = [
  {
    q: "What is the Commodity Market Movers Roundtable & Awards?",
    a: "The annual closed-door evening for the senior leaders of India's commodity trade — five business sessions, a keynote, 17 industry honours and a gala dinner. The 2nd edition takes place on 1 October 2026 at The Westin Mumbai Powai Lake. Convened by Tefla's.",
  },
  {
    q: "When and where is CMM 2026 happening?",
    a: "Thursday, 1 October 2026, from 3:30 PM IST onwards, at The Westin Mumbai Powai Lake, India.",
  },
  {
    q: "Who can nominate for a Commodity Market Movers Award?",
    a: "Both self-nominations and third-party nominations are accepted. The independent jury reviews against growth, leadership, market-firsts, innovation, sustainability and risk-management criteria. Nominators do not need to be delegates.",
  },
  {
    q: "How much does an award nomination cost?",
    a: "For the 2026 edition, award nominations are COMPLIMENTARY — free to submit. Every nomination includes a seat at the **gala dinner** and full jury consideration — yours whether the nomination wins or not.",
  },
  {
    q: "How much does a delegate pass cost?",
    a: "For the 2026 edition, the delegate pass is COMPLIMENTARY — free to attend. The pass covers all five business sessions, the keynote, the awards ceremony and senior networking. Note: the gala dinner is reserved for award nominees and sponsors. Seats are limited and allocated first-come, first-served.",
  },
  {
    q: "What categories can I nominate for?",
    a: "17 honours across three houses — Agri Commodities (6 honours including Edible Oil, Grain & Feed, Sugar & Bioenergy, Sustainability), Non-Agri Commodities (5 honours including Bullion & Precious Metals, Metals & Mining, Energy & Industrial, Commodity Tech), and Special Recognition (6 honours including Lifetime Contribution, Women Leadership, Young Leader, Global Trade, Supply Chain & Logistics, Risk & Market Intelligence).",
  },
  {
    q: "Who attends the Commodity Market Movers event?",
    a: "Roughly 200 senior decision-makers across the Indian commodity chain — traders, importers and exporters, refiners and processors, exchanges and brokers, banks and financiers, logistics and shipping firms, risk and analytics firms, policymakers and associations, investors and funds, and tech & market-intelligence leaders.",
  },
  {
    q: "Can my company sponsor or exhibit at CMM 2026?",
    a: "Yes. Multiple sponsorship slabs and exhibit options are available, designed for trading houses, exchanges, banks, refiners, logistics firms and commodity-tech companies. Email events@teflas.com for the full partner deck.",
  },
  {
    q: "How do I contact the organisers?",
    a: "Email events@teflas.com, WhatsApp +91 98338 55566, or call +91 98338 55566 / +91 98337 55566. Office: 301 AB & 308, Abhishek Premises, C-5 Dalia Industrial Estate, Andheri (W), Mumbai – 400053.",
  },
];

/* ─────────────  Helpers  ───────────── */
const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  show: { opacity: 1, y: 0, transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = (delay = 0.08) => ({
  hidden: {},
  show: { transition: { staggerChildren: delay, delayChildren: 0.05 } },
});

const useCountdown = (targetIso) => {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    const target = new Date(targetIso).getTime();
    const tick = () => {
      const diff = Math.max(0, target - Date.now());
      setT({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff / 3600000) % 24),
        m: Math.floor((diff / 60000) % 60),
        s: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetIso]);
  return t;
};

const Counter = ({ to, duration = 1.4, suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start;
    const startVal = 0;
    const animate = (ts) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / (duration * 1000));
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.floor(startVal + (to - startVal) * eased));
      if (p < 1) requestAnimationFrame(animate);
      else setVal(to);
    };
    requestAnimationFrame(animate);
  }, [inView, to, duration]);
  return <span ref={ref}>{val}{suffix}</span>;
};

const MagneticButton = ({ children, className = "", as = "a", ...props }) => {
  const ref = useRef(null);
  const x = useSpring(0, { stiffness: 220, damping: 18 });
  const y = useSpring(0, { stiffness: 220, damping: 18 });
  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    x.set((e.clientX - cx) * 0.25);
    y.set((e.clientY - cy) * 0.25);
  };
  const onLeave = () => { x.set(0); y.set(0); };
  const Comp = as === "button" ? motion.button : motion.a;
  return (
    <Comp
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ x, y }}
      className={className}
      {...props}
    >
      {children}
    </Comp>
  );
};

/* ─────────────  Scroll progress  ───────────── */
const ScrollProgress = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 25, restDelta: 0.001 });
  return <motion.div className="scroll-progress" style={{ scaleX, width: "100%" }} />;
};

/* ─────────────  Custom cursor  ───────────── */
const CustomCursor = () => {
  const dotX = useMotionValue(-100);
  const dotY = useMotionValue(-100);
  const ringX = useSpring(dotX, { stiffness: 200, damping: 24 });
  const ringY = useSpring(dotY, { stiffness: 200, damping: 24 });

  useEffect(() => {
    const move = (e) => { dotX.set(e.clientX - 3); dotY.set(e.clientY - 3); };
    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, [dotX, dotY]);

  return (
    <>
      <motion.div className="cursor-dot" style={{ x: dotX, y: dotY }} />
      <motion.div className="cursor-ring" style={{ x: ringX, y: ringY, translateX: -15, translateY: -15 }} />
    </>
  );
};

/* ─────────────  Nav  ───────────── */
const Nav = ({ onLeadOpen, onNominate, onRegister }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#050b14]/75 backdrop-blur-2xl border-b border-white/5" : "bg-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 flex items-center justify-between h-[84px]">
        <a href="#top" className="flex items-center gap-3 logo-shimmer" data-testid="nav-logo">
          <img
            src="/assets/cmm-logo.svg"
            alt="Commodity Market Movers"
            className="h-12 lg:h-14 w-auto"
            style={{ filter: "brightness(0) invert(1)" }}
          />
        </a>
        <nav className="hidden lg:flex items-center gap-5 xl:gap-6">
          {NAV_LINKS.map(([l, h]) => (
            <a
              key={h}
              href={h}
              className="text-[0.92rem] text-white/75 hover:text-[#00d1ff] transition-colors relative group"
              data-testid={`nav-link-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              {l}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#00d1ff] group-hover:w-full transition-all duration-300" />
            </a>
          ))}
          <button onClick={() => onLeadOpen("brochure")} className="text-[0.92rem] text-white/75 hover:text-[#f5c518] transition-colors" data-testid="nav-brochure">
            Brochure
          </button>
          <MagneticButton as="button" onClick={onNominate} className="btn btn-cyan !py-2.5 !px-5 text-sm relative" data-testid="nav-nominate">
            <span className="relative flex items-center gap-2">
              <span className="relative flex w-2 h-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
              </span>
              Nominate
            </span>
          </MagneticButton>
          <MagneticButton as="button" onClick={onRegister} className="btn btn-gold !py-2.5 !px-5 text-sm font-extrabold uppercase tracking-wider" data-testid="nav-cta-register">
            Get my free pass →
          </MagneticButton>
        </nav>
        <button className="lg:hidden text-white p-2" onClick={() => setOpen((v) => !v)} aria-label="Menu" data-testid="nav-hamburger">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {open ? <path d="M18 6L6 18M6 6l12 12" /> : <><path d="M3 6h18" /><path d="M3 12h18" /><path d="M3 18h18" /></>}
          </svg>
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#050b14]/97 backdrop-blur-2xl border-t border-white/5 px-5 py-5 space-y-3 overflow-hidden"
            data-testid="mobile-menu"
          >
            {NAV_LINKS.map(([l, h]) => (
              <a key={h} href={h} onClick={() => setOpen(false)} className="block text-white/85 py-2">{l}</a>
            ))}
            <button onClick={() => { setOpen(false); onRegister?.(); }} className="btn btn-gold w-full mt-2">Register free →</button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

/* ─────────────  Kinetic heading  ───────────── */
const Kinetic = ({ words, italicIndex, accent = "cyan" }) => (
  <h2 className="font-display text-2xl sm:text-3xl lg:text-[2.4rem] mt-8 font-light text-white/85" style={{ letterSpacing: "-0.025em", lineHeight: 1.15 }}>
    {words.map((w, i) => (
      <span key={i} className="inline-block overflow-hidden align-baseline" style={{ marginRight: "0.22em" }}>
        <motion.span
          className="inline-block"
          initial={{ y: "110%" }}
          animate={{ y: 0 }}
          transition={{ duration: 0.85, delay: 0.85 + i * 0.06, ease: [0.22, 1, 0.36, 1] }}
        >
          {i === italicIndex ? (
            <span className={`italic ${accent === "cyan" ? "accent-cyan" : "accent-gold"}`}>{w}</span>
          ) : (
            w
          )}
        </motion.span>
      </span>
    ))}
  </h2>
);

/* ─────────────  Hero  ───────────── */
const Hero = ({ onNominate, onRegister }) => {
  const cd = useCountdown(EVENT_ISO);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const blobY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const fadeOut = useTransform(scrollYProgress, [0, 0.8], [1, 0.4]);

  return (
    <section id="top" ref={heroRef} className="relative pt-28 pb-16 lg:pt-44 lg:pb-28 overflow-hidden grain">
      <motion.div style={{ y: blobY }} className="absolute inset-0">
        <div className="blob blob-gold w-[620px] h-[620px] -top-32 -left-32 opacity-50" />
        <div className="blob blob-gold w-[460px] h-[460px] top-40 right-[-180px] opacity-35" />
        <div className="blob blob-gold w-[400px] h-[400px] bottom-[-180px] left-1/3 opacity-25" />
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-[1.1fr_0.95fr] gap-14 items-center">
        <div>
          {/* Brand mark line */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="flex items-center gap-3 flex-wrap"
            data-testid="hero-eyebrow"
          >
            <span className="hero-brandmark">Commodity Market Movers</span>
            <span className="hero-pill">2nd Edition</span>
            <span className="hero-pill">1 October 2026</span>
            <span className="hero-pill">Mumbai</span>
            <span className="hero-pill" style={{ background: "rgba(245,197,24,0.18)", borderColor: "rgba(245,197,24,0.55)", color: "#f5c518" }} data-testid="hero-free-pill">FREE entry</span>
          </motion.div>

          {/* Big brand-positioning headline */}
          <h2 className="hero-headline" data-testid="hero-headline">
            <span className="inline-block overflow-hidden align-baseline" style={{ marginRight: "0.22em" }}>
              <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.95, delay: 0.35, ease: [0.22, 1, 0.36, 1] }} className="inline-block">The</motion.span>
            </span>
            <span className="inline-block overflow-hidden align-baseline" style={{ marginRight: "0.22em" }}>
              <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.95, delay: 0.42, ease: [0.22, 1, 0.36, 1] }} className="inline-block">annual</motion.span>
            </span>
            <span className="inline-block overflow-hidden align-baseline" style={{ marginRight: "0.22em" }}>
              <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.95, delay: 0.49, ease: [0.22, 1, 0.36, 1] }} className="inline-block">room</motion.span>
            </span>
            <span className="inline-block overflow-hidden align-baseline" style={{ marginRight: "0.22em" }}>
              <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.95, delay: 0.56, ease: [0.22, 1, 0.36, 1] }} className="inline-block">that</motion.span>
            </span>
            <span className="inline-block overflow-hidden align-baseline" style={{ marginRight: "0.22em", paddingRight: "0.08em" }}>
              <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.95, delay: 0.63, ease: [0.22, 1, 0.36, 1] }} className="inline-block italic font-light accent-grad" style={{ fontWeight: 300, paddingRight: "0.12em" }}>moves</motion.span>
            </span>
            <span className="inline-block overflow-hidden align-baseline" style={{ marginRight: "0.22em" }}>
              <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.95, delay: 0.7, ease: [0.22, 1, 0.36, 1] }} className="inline-block">India's</motion.span>
            </span>
            <span className="inline-block overflow-hidden align-baseline" style={{ marginRight: "0.22em" }}>
              <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.95, delay: 0.77, ease: [0.22, 1, 0.36, 1] }} className="inline-block">commodity</motion.span>
            </span>
            <span className="inline-block overflow-hidden align-baseline">
              <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.95, delay: 0.84, ease: [0.22, 1, 0.36, 1] }} className="inline-block">markets.</motion.span>
            </span>
          </h2>

          {/* Brand tagline — from original brief */}
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.0 }}
            className="hero-tagline"
          >
            <span>The Influencers.</span> <span>The Strategists.</span> <span>The Market Leaders.</span>
          </motion.p>

          {/* Format pills — roundtable + awards + gala as small chips, not screaming labels */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.1 }}
            className="mt-7 flex flex-wrap gap-2"
            data-testid="hero-format-pills"
          >
            <span className="format-pill"><span className="dot bg-[#f5c518]" /> Roundtable · 3 market sessions</span>
            <span className="format-pill"><span className="dot bg-[#f5c518]" /> Awards · 17 honours</span>
            <span className="format-pill"><span className="dot bg-white/70" /> Gala dinner</span>
          </motion.div>

          {/* Body */}
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="mt-6 lg:mt-7 text-[0.98rem] lg:text-lg text-white/65 max-w-xl leading-relaxed"
          >
            An invite-led evening for the senior leaders of India's commodity trade — traders, refiners, financiers and policymakers — in the same room, off the record. Convened by Tefla's.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.35 }}
            className="mt-6 lg:mt-9 flex flex-wrap gap-3"
            data-testid="hero-ctas"
          >
            <MagneticButton as="button" onClick={onRegister} className="btn btn-gold pulse-gold !text-base !px-7 !py-4 font-extrabold uppercase tracking-wider" data-testid="hero-cta-reserve">
              Claim Complimentary Pass →
            </MagneticButton>
            <MagneticButton as="button" onClick={onNominate} className="btn btn-ghost !border-[#f5c518]/50 !text-[#f5c518] hover:!bg-[#f5c518]/10" data-testid="hero-cta-nominate">
              Nominate for an award →
            </MagneticButton>
          </motion.div>

          {/* Trust pills under primary CTA */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.5 }}
            className="mt-4 lg:mt-5 flex items-center gap-4 flex-wrap text-[0.7rem] uppercase tracking-[0.18em] font-bold text-[#f5c518]"
            data-testid="hero-trust-pills"
          >
            <span className="flex items-center gap-1.5"><span className="text-[#f5c518]">✓</span> 100% Free</span>
            <span className="flex items-center gap-1.5"><span className="text-[#f5c518]">✓</span> Limited Seats</span>
            <span className="flex items-center gap-1.5"><span className="text-[#f5c518]">✓</span> Priority Entry</span>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="show"
            variants={stagger(0.12)}
            transition={{ delayChildren: 1.55 }}
            className="mt-7 lg:mt-9 grid grid-cols-3 gap-4 max-w-xl"
          >
            {[["Date", "1 October 2026"], ["Venue", "The Westin Mumbai Powai Lake"], ["From", "3:30 PM onwards"]].map(([k, v]) => (
              <motion.div key={k} variants={fadeUp} className="border-l-2 border-[#f5c518] pl-3" data-testid={`hero-meta-${k.toLowerCase()}`}>
                <div className="text-[0.62rem] sm:text-[0.68rem] uppercase tracking-[0.22em] text-[#f5c518] font-bold">{k}</div>
                <div className="text-white font-medium text-[0.82rem] sm:text-[0.95rem] leading-snug mt-1">{v}</div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.7 }}
            className="mt-10 flex items-center gap-3 flex-wrap text-sm"
            data-testid="hero-countdown"
          >
            <span className="text-white/55 uppercase tracking-[0.2em] text-[0.68rem] font-bold">Event in</span>
            <div className="flex items-center gap-2">
              {[["d", cd.d, "days"], ["h", cd.h, "hrs"], ["m", cd.m, "min"], ["s", cd.s, "sec"]].map(([k, v, lbl]) => (
                <div key={k} className="surface px-3 py-2 min-w-[62px] text-center">
                  <div className="font-display text-xl text-[#f5c518] tabular-nums leading-none font-bold">{String(v).padStart(2, "0")}</div>
                  <div className="text-[0.6rem] uppercase tracking-wider text-white/55 mt-1">{lbl}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        <motion.div style={{ opacity: fadeOut }} className="relative">
          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}>
            <div className="absolute -inset-3 bg-gradient-to-tr from-[#f5c518]/40 via-[#f5c518]/10 to-[#f5c518]/30 rounded-[28px] blur-2xl" />
            <div className="relative rounded-[24px] overflow-hidden border border-[#f5c518]/30 aspect-[5/6]">
              <motion.img
                style={{ y: imgY }}
                src={HERO_IMG}
                alt="Commodity Market Movers — 1st Edition gala moment"
                className="w-full h-[115%] object-cover absolute inset-0"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050b14] via-[#050b14]/40 to-transparent" />
              <div className="absolute top-5 right-5 bg-[#f5c518] text-[#050b14] rounded-full px-3.5 py-1.5 text-xs font-extrabold tracking-wide uppercase">
                2nd Edition · 2026
              </div>
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                <div>
                  <div className="text-[0.68rem] uppercase tracking-[0.18em] text-[#f5c518] font-bold">1st Edition · 2025</div>
                  <div className="font-display text-white text-lg mt-1 font-bold">A room that shaped the year ahead.</div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Organiser strip */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.8 }}
        className="relative max-w-7xl mx-auto px-5 lg:px-8 mt-16 lg:mt-20"
      >
        <div className="surface px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-5" data-testid="organiser-strip">
          <div className="flex items-center gap-5">
            <div className="text-[0.7rem] uppercase tracking-[0.22em] text-white/55 font-bold">An initiative by</div>
            <img src="/assets/teflas.svg" alt="Tefla's" className="h-9 w-auto" />
          </div>
          <div className="hidden sm:block w-px h-8 bg-white/10" />
          <div className="flex items-center gap-5">
            <div className="text-[0.7rem] uppercase tracking-[0.22em] text-white/55 font-bold">Knowledge Partner</div>
            <img src="/assets/mc3.png" alt="McQube" className="h-9 w-auto" />
          </div>
        </div>
      </motion.div>
    </section>
  );
};

/* ─────────────  Why Attend (Meta-ad benefit grid)  ───────────── */
const WhyAttend = ({ onRegister }) => (
  <section id="why" className="relative py-20 lg:py-24 bg-[#fff8dc]" data-testid="why-attend">
    <div className="max-w-7xl mx-auto px-5 lg:px-8">
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={stagger(0.1)} className="text-center max-w-3xl mx-auto">
        <motion.div variants={fadeUp}>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#050b14] text-[#f5c518] text-[0.65rem] font-bold tracking-[0.22em] uppercase">For Senior Commodity Leaders</span>
        </motion.div>
        <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl lg:text-5xl mt-5 font-extrabold uppercase text-[#050b14]" style={{ fontFamily: "'Bricolage Grotesque', sans-serif", letterSpacing: "-0.02em", lineHeight: 1.05 }}>
          Why attend <span className="text-[#c89c00]">Commodity Market Movers?</span>
        </motion.h2>
      </motion.div>

      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} variants={stagger(0.12)} className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { icon: "💡", title: "Actionable Insights", body: "Real market intelligence from the people moving India's commodity trade." },
          { icon: "📈", title: "Market Outlook", body: "The trends shaping agri, metals, bullion and energy in the next 12 months." },
          { icon: "🤝", title: "Network & Connect", body: "Senior, cross-sector room. Off the record, on the record afterwards." },
          { icon: "🎯", title: "Stay Ahead", body: "Position yourself, your firm, and your year before the next big move." },
        ].map((c) => (
          <motion.div key={c.title} variants={fadeUp} className="bg-white rounded-2xl p-6 border border-[#050b14]/10 hover:border-[#f5c518] hover:shadow-lg" style={{ transition: "border-color 0.2s, box-shadow 0.2s" }}>
            <div className="w-12 h-12 rounded-xl bg-[#fff8dc] border border-[#f5c518]/40 flex items-center justify-center text-2xl">{c.icon}</div>
            <div className="font-display font-extrabold text-lg mt-4 text-[#050b14]" style={{ letterSpacing: "-0.02em" }}>{c.title}</div>
            <p className="text-[#050b14]/65 text-sm mt-2 leading-relaxed">{c.body}</p>
          </motion.div>
        ))}
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mt-12 text-center">
        <button onClick={onRegister} className="btn btn-gold !text-base !px-7 !py-4 font-extrabold uppercase tracking-wider" data-testid="why-cta-reserve">
          Claim Complimentary Pass →
        </button>
        <div className="mt-3 text-[0.7rem] uppercase tracking-[0.18em] font-bold text-[#050b14]/55">100% Free · Limited Seats · Priority Entry</div>
      </motion.div>
    </div>
  </section>
);

/* ─────────────  Marquee strip  ───────────── */
const MarqueeStrip = () => (
  <div className="marquee-row" data-testid="marquee-strip">
    <Marquee speed={45} gradient={false}>
      {Array(4).fill(0).map((_, i) => (
        <span key={i} className="marquee-item">
          <span className="solid">Roundtable</span> <span className="dot" /> <span className="amp-mq">&</span> <span className="dot" /> <span className="solid solid-gold">Awards</span> <span className="dot" /> 2026 <span className="dot" />
        </span>
      ))}
    </Marquee>
  </div>
);

/* ─────────────  Two Halves (Roundtable + Awards)  ───────────── */
const TwoHalves = ({ onNominate }) => (
  <section className="relative py-20 lg:py-24 overflow-hidden" data-testid="two-halves">
    <div className="blob blob-cyan w-[400px] h-[400px] -left-32 top-1/4 opacity-25" />
    <div className="blob blob-gold w-[420px] h-[420px] -right-32 bottom-0 opacity-25" />
    <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={stagger(0.1)} className="text-center max-w-3xl mx-auto">
        <motion.div variants={fadeUp}><span className="eyebrow" style={{ justifyContent: "center" }}>One evening · Two halves</span></motion.div>
        <motion.h2 variants={fadeUp} className="h-display text-4xl lg:text-6xl mt-6">
          A <span className="accent-cyan italic font-light" style={{ fontWeight: 300 }}>roundtable</span> first.<br />
          The <span className="accent-gold italic font-light" style={{ fontWeight: 300 }}>awards</span> after.
        </motion.h2>
        <motion.p variants={fadeUp} className="text-white/65 mt-6 text-[1.05rem] leading-relaxed">
          The market sets the temperature. Then the room toasts the year's best.
        </motion.p>
      </motion.div>

      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} variants={stagger(0.18)} className="mt-14 grid md:grid-cols-2 gap-6">
        <motion.div variants={fadeUp} className="half-card half-roundtable group">
          <div className="half-icon"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a14 14 0 010 18M12 3a14 14 0 000 18"/></svg></div>
          <div className="half-tag">Part I · 3:00 – 7:45 PM</div>
          <h3 className="half-title">The Roundtable</h3>
          <p className="half-desc">A future-focused keynote and five business sessions — pulses, grains, energy, metals & bullion, risk — with the people who actually move the prices.</p>
          <ul className="half-list">
            {["Agri Commodity Outlook","Non-Agri Outlook","Risk, Logistics & Supply Chains","Keynote — The road ahead"].map((x) => (
              <li key={x}><span>▸</span>{x}</li>
            ))}
          </ul>
          <a href="#agenda" className="half-link">See the full programme →</a>
        </motion.div>

        <motion.div variants={fadeUp} className="half-card half-awards group">
          <div className="half-icon"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M8 21h8M12 17v4M7 4h10v4a5 5 0 01-10 0V4z"/><path d="M5 6H3v2a3 3 0 003 3M19 6h2v2a3 3 0 01-3 3"/></svg></div>
          <div className="half-tag">Part II · 7:45 PM onwards</div>
          <h3 className="half-title">The Awards</h3>
          <p className="half-desc">Seventeen honours across three houses — Agri, Non-Agri and Special Recognition — followed by cocktails and the gala dinner.</p>
          <ul className="half-list">
            {["6 Agri Commodity Honours","5 Non-Agri Commodity Honours","6 Special Recognition Honours","Gala dinner + senior networking"].map((x) => (
              <li key={x}><span>★</span>{x}</li>
            ))}
          </ul>
          <div className="flex items-center gap-4 flex-wrap">
            <a href="#awards" className="half-link gold">See all 17 honours →</a>
            <button onClick={onNominate} className="btn btn-gold !py-2 !px-4 text-xs" data-testid="halves-nominate">Nominate now →</button>
          </div>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

/* ─────────────  Evening (Why)  ───────────── */
const Evening = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <section id="experience" ref={ref} className="relative py-24 lg:py-32 bg-[#0a1326] overflow-hidden">
      <div className="blob blob-cyan w-[420px] h-[420px] -left-32 top-1/3 opacity-25" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">

        {/* LEFT — copy */}
        <div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-100px" }} variants={stagger(0.1)}>
            <motion.div variants={fadeUp}><span className="eyebrow">Why this evening</span></motion.div>
            <motion.h2 variants={fadeUp} className="h-display text-4xl lg:text-[3.4rem] mt-6">
              Markets move on relationships<br />
              as much as <span className="accent-gold italic font-light" style={{ fontWeight: 300 }}>numbers</span>.
            </motion.h2>
            <motion.p variants={fadeUp} className="text-white/75 mt-6 text-[1.05rem] lg:text-lg leading-relaxed max-w-xl">
              Prices are being rewritten by things no spreadsheet fully captures — weather, war, shifting trade routes, the slow rebuild of supply chains. India sits closer to the centre of all of it than it ever has.
            </motion.p>
            <motion.p variants={fadeUp} className="text-white/65 mt-5 text-[1rem] leading-relaxed max-w-xl">
              An evening built for the people living that reality day to day — small, senior, mixed-sector. Five business sessions, a keynote, awards and a dinner where the actual business gets done. No badges-and-booths trade show.
            </motion.p>
          </motion.div>

          {/* Stats row — compact under copy */}
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-100px" }} variants={stagger(0.12)} className="grid grid-cols-3 gap-3 sm:gap-4 mt-10 max-w-xl" data-testid="stats-row">
            {[
              [3, "Market sessions"],
              [17, "Award honours"],
              [1, "Evening that pays for itself"],
            ].map(([n, l]) => (
              <motion.div key={l} variants={fadeUp} className="surface p-4 sm:p-5 relative overflow-hidden">
                <div className="font-display num-grad text-4xl sm:text-5xl font-extrabold leading-none" style={{ letterSpacing: "-0.05em" }}>
                  <Counter to={n} />
                </div>
                <div className="text-white/65 mt-2 text-[0.78rem] sm:text-[0.85rem] leading-tight">{l}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* RIGHT — image placeholder */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
          data-testid="evening-image"
        >
          <div className="absolute -inset-4 bg-gradient-to-tr from-[#00d1ff]/20 via-transparent to-[#f5c518]/20 rounded-[28px] blur-2xl pointer-events-none" />
          <div className="relative rounded-[22px] overflow-hidden border border-white/10 aspect-[4/5] lg:aspect-[5/6] bg-[#0f1c36]">
            <motion.img
              style={{ y: imgY, height: "115%" }}
              src={EVENING_IMG}
              alt="1st Edition — the room"
              className="w-full object-cover absolute inset-0"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050b14]/85 via-[#050b14]/10 to-transparent pointer-events-none" />
            {/* Floating caption card */}
            <div className="absolute top-5 left-5">
              <div className="bg-[#050b14]/70 backdrop-blur-md border border-white/10 rounded-full px-3 py-1.5 text-[0.65rem] uppercase tracking-[0.2em] text-[#00d1ff] font-bold">
                1st Edition · 2025
              </div>
            </div>
            <div className="absolute bottom-6 left-6 right-6">
              <div className="font-display text-white text-2xl lg:text-3xl font-bold leading-tight" style={{ letterSpacing: "-0.03em" }}>
                The room, in one frame.
              </div>
              <div className="text-white/65 text-sm mt-1">The whole commodity chain, in one evening.</div>
            </div>
          </div>
          {/* Decorative corner tag */}
          <div className="absolute -bottom-4 -left-4 hidden lg:flex items-center gap-2 bg-[#050b14] border border-[#00d1ff]/30 rounded-full px-4 py-2">
            <span className="w-2 h-2 rounded-full bg-[#00d1ff] animate-pulse" />
            <span className="text-[0.7rem] uppercase tracking-[0.2em] text-white/75 font-bold">By invite · 200 seats</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/* ─────────────  Agenda  ───────────── */
const PersonChip = ({ name, gold = false }) => {
  const s = SPEAKER_BY_NAME[name];
  return (
    <span className={`inline-flex flex-col px-3 py-1.5 rounded-xl border text-left ${gold ? "bg-[#f5c518]/10 border-[#f5c518]/40" : "bg-white/5 border-white/8 hover:border-[#00d1ff]/40"} transition-colors`} data-testid={`agenda-person-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <span className={`text-[0.82rem] font-semibold ${gold ? "text-[#f5c518]" : "text-white/90"}`}>{name}</span>
      {s && <span className="text-[0.68rem] text-white/50 leading-snug">{s.role}</span>}
    </span>
  );
};

const AgendaRow = ({ row, i }) => {
  const isBreak = row.kind === "break" || row.kind === "awards";
  return (
    <motion.div variants={fadeUp} className="group grid grid-cols-[96px_1fr] sm:grid-cols-[150px_1fr] gap-5 py-7 border-t border-white/8 first:border-t-0 relative" data-testid={`agenda-row-${i}`}>
      <div className="relative pt-1">
        <div className={`font-display text-2xl sm:text-3xl font-extrabold ${row.kind === "awards" ? "text-[#f5c518]" : "text-[#00d1ff]"}`} style={{ letterSpacing: "-0.04em" }}>{row.time}</div>
        <div className="text-white/40 text-xs uppercase tracking-wider mt-1">{row.end === "onwards" ? "onwards" : `– ${row.end}`}</div>
        <span className={`hidden sm:block absolute right-[-8px] top-3 w-2.5 h-2.5 rounded-full ${row.kind === "awards" ? "bg-[#f5c518]" : "bg-[#00d1ff]"} group-hover:scale-150 transition-transform duration-300`} style={{ boxShadow: `0 0 18px ${row.kind === "awards" ? "rgba(245,197,24,0.7)" : "rgba(0,209,255,0.7)"}` }} />
      </div>
      <div>
        {row.track && <div className="text-[0.62rem] uppercase tracking-[0.2em] font-bold text-[#00d1ff]/80 mb-2">{row.track}</div>}
        <div className={`text-white text-lg sm:text-xl font-semibold ${isBreak ? "" : "font-display"}`}>{row.title}</div>
        {row.theme && <div className="italic text-[#f5c518]/90 mt-1.5 text-[0.98rem] leading-snug">{row.theme}</div>}
        {row.note && <div className="italic text-white/55 mt-1 text-[0.95rem]">{row.note}</div>}
        {row.moderator && (
          <div className="mt-4">
            <div className="text-[0.62rem] uppercase tracking-[0.2em] font-bold text-white/40 mb-2">Moderator</div>
            <PersonChip name={row.moderator} gold />
          </div>
        )}
        {row.panel && (
          <div className="mt-4">
            <div className="text-[0.62rem] uppercase tracking-[0.2em] font-bold text-white/40 mb-2">{row.kind === "keynote" ? "Speakers" : "Guest panelists"}</div>
            <div className="flex flex-wrap gap-2">{row.panel.map((p) => <PersonChip key={p} name={p} />)}</div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

const Agenda = () => (
  <section id="agenda" className="relative py-24 lg:py-32 overflow-hidden">
    <div className="blob blob-cyan w-[400px] h-[400px] top-1/3 -right-40 opacity-30" />
    <div className="relative max-w-6xl mx-auto px-5 lg:px-8">
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={stagger(0.1)} className="max-w-3xl">
        <motion.div variants={fadeUp}><span className="eyebrow">Event outline · Thursday, 1 October 2026</span></motion.div>
        <motion.h2 variants={fadeUp} className="h-display text-4xl lg:text-6xl mt-6">
          Five business sessions, a keynote, and the <span className="accent-cyan italic font-light" style={{ fontWeight: 300 }}>awards night</span>.
        </motion.h2>
        <motion.p variants={fadeUp} className="text-white/65 mt-5 text-lg">Doors at 2:45 PM · Opening & keynote at 3:00 PM · Felicitations, awards, cocktails & dinner from 7:45 PM.</motion.p>
      </motion.div>

      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} variants={stagger(0.08)} className="mt-14 relative">
        <div className="absolute left-[46px] sm:left-[82px] top-2 bottom-2 w-px bg-gradient-to-b from-[#00d1ff]/60 via-[#00d1ff]/20 to-[#f5c518]/40 hidden sm:block" />
        {AGENDA.map((row, i) => <AgendaRow key={i} row={row} i={i} />)}
      </motion.div>
      <p className="text-white/40 text-xs italic mt-8">Programme subject to minor changes. Speaker participation confirmed at time of publishing.</p>
    </div>
  </section>
);

/* ─────────────  Speakers — "Learn from Industry Leaders"  ───────────── */
const P = "/assets/speakers/";
const SPEAKERS = [
  // Tier 1 — Chairmen / CMDs / Presidents
  { name: "Atul Chaturvedi", role: "Executive Chairman, Shree Renuka Sugars Ltd", photo: P + "atul-chaturvedi.jpg", tag: "Keynote · Moderator" },
  { name: "Sanjeev Asthana", role: "CEO, Patanjali Foods & President, SEA", photo: P + "sanjeev-asthana.jpg" },
  { name: "Bimal Bengani", role: "Chairman, Bengani Group of Companies", photo: P + "bimal-bengani.jpg" },
  { name: "Nagaraj Meda", role: "CMD, Transgraph Consulting Pvt. Ltd.", photo: P + "nagaraj-meda.png", tag: "Keynote" },
  { name: "J.K. Chaturvedi", role: "CMD, Asian Petroproducts & Exports Ltd.", photo: P + "jk-chaturvedi.png" },
  // Moderators
  { name: "Manisha Gupta", role: "Group Commodities Editor, CNBC-TV18", photo: P + "manisha-gupta.jpg", tag: "Moderator" },
  { name: "Mrituenjay Jha", role: "Commodity Editor & Anchor, Zee Business", photo: P + "mrituenjay-jha.jpg", tag: "Moderator" },
  { name: "G. Chandrashekhar", role: "Economist, Senior Journalist & Policy Commentator", photo: P + "g-chandrashekhar.png", tag: "Moderator" },
  { name: "Deepak Pareek", role: "Founder, HnyB Tech-Incubations Pvt Ltd", photo: P + "deepak-pareek.jpg", tag: "Keynote · Moderator" },
  // Tier 2 — MDs / CEOs / EDs / Managing Partners / CXOs
  { name: "Mrugank Paranjape", role: "Managing Partner, MCQube", photo: P + "mrugank-paranjape.jpg", tag: "Keynote" },
  { name: "Dr. B.V. Mehta", role: "Executive Director, SEA", photo: P + "bv-mehta.png" },
  { name: "Ravi Gupta", role: "Executive Director, Shree Renuka Sugars Ltd", photo: P + "ravi-gupta.jpg" },
  { name: "Sandeep Bajoria", role: "CEO, Sunvin Group", photo: P + "sandeep-bajoria.jpg" },
  { name: "Rahil Sheikh", role: "Managing Director, MEIR Commodities", photo: P + "rahil-sheikh.jpg" },
  { name: "Ankush Jain", role: "CEO, Shree Kedar Agro Nutritec" },
  { name: "Sandeep Daga", role: "Founder & CEO, Regsus Consulting", photo: P + "sandeep-daga.png" },
  { name: "Aurobinda Gayan", role: "Founder & CEO, Bluglance Consulting", photo: P + "aurobinda-gayan.png" },
  { name: "Alok Ranjan", role: "Chief Marketing Officer, Vedanta", photo: P + "alok-ranjan.jpg" },
  { name: "Binod Agarwal", role: "Managing Partner, SGR Agristeel LLP", photo: P + "binod-agarwal.jpg" },
  // Tier 3 — Directors / Partners / Founders
  { name: "Sunil Kumar Singh", role: "Founding Partner, SS Agrovision LLP & Former Joint MD, NAFED", photo: P + "sunil-kumar-singh.png" },
  { name: "Anurag Saraogi", role: "Director, BPCL", photo: P + "anurag-saraogi.jpg" },
  { name: "Anuj Jain", role: "Director (Finance), IOCL", photo: P + "anuj-jain.jpg" },
  { name: "Dr. V. Shunmugam", role: "Partner, MCQube", photo: P + "dr-v-shunmugam.jpg" },
  { name: "Dr. Sudharanjan Sahu", role: "Country Representative, Swiss Singapore Overseas Ltd", photo: P + "sudharanjan-sahu.png" },
  { name: "Sumit Gupta", role: "Director, Waseda Global", photo: P + "sumit-gupta.jpg" },
  { name: "Shailesh Tulsiyan", role: "Director, Vinayak Impex FZE", photo: P + "shailesh-tulsiyan.jpg" },
  { name: "Rahul Chauhan", role: "Director, IGrain India", photo: P + "rahul-chauhan.jpg" },
  { name: "Gaurav Jain", role: "Founder, AgPulse Analytica", photo: P + "gaurav-jain.jpg" },
  { name: "Vijay Sardana", role: "Advocate & Techno-legal Expert · Trade & Investment Strategist · Agri-Economist", photo: P + "vijay-sardana.png" },
  { name: "Aadil Singh", role: "Director, Tefla's · Convenor", photo: P + "aadil-singh.jpg", tag: "Keynote · Host" },
  // Tier 4 — Business Heads / EVPs / Heads
  { name: "Somnath Chatterjee", role: "Executive Vice President, ITC", photo: P + "somnath-chatterjee.jpg" },
  { name: "Dilip Mohanty", role: "Executive Vice President, Reliance Retail Ltd", photo: P + "dilip-mohanty.jpg" },
  { name: "Chittaranjan Rege", role: "Head — Base Metals, Multi Commodity Exchange of India (MCX)", photo: P + "chittaranjan-rege.jpg" },
  { name: "Kunal Karan Parakh", role: "Business Head, Full Circle Commodities" },
  { name: "Vikram Dhawan", role: "Head — Commodities, Nippon India Mutual Fund", photo: P + "vikram-dhawan.jpg" },
  { name: "Deepak Mehta", role: "Indian Gas Exchange (IGX)", photo: P + "deepak-mehta.jpg" },
  // Tier 5 — AVPs / Senior Managers / Analysts
  { name: "Hitesh Sethia", role: "AVP — Commodities, NSE", photo: P + "hitesh-sethia.jpg" },
  { name: "Manav Modi", role: "AVP & Commodity Analyst, Motilal Oswal Financial Services", photo: P + "manav-modi.jpg" },
  { name: "Barleen Kaur", role: "Senior Client Development Manager, S&P Global Energy", photo: P + "barleen-kaur.jpg" },
  { name: "Rajesh Paharia", role: "Chief Manager — Business Development, KRIBHCO", photo: P + "rajesh-paharia.jpg" },
  { name: "Yogesh Shivanna", role: "Senior Trader — Pulses, ADM Beans & Pulses" },
];
const SPEAKER_BY_NAME = Object.fromEntries(SPEAKERS.map((s) => [s.name, s]));

const initials = (full) => {
  const parts = full.replace(/dr\.?|mr\.?|ms\.?/i, "").trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts[parts.length - 1]?.[0] || "")).toUpperCase();
};

const SpeakerCard = ({ name, role, photo, tag = "Speaker" }) => {
  const [imgOk, setImgOk] = useState(true);
  return (
    <motion.div variants={fadeUp} className="group" data-testid={`speaker-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="relative aspect-[3/4] rounded-[18px] overflow-hidden bg-[#0a1326] border border-white/10 group-hover:border-[#f5c518]/50" style={{ transition: "border-color 0.25s" }}>
        {photo && imgOk ? (
          <img
            src={photo}
            alt={name}
            loading="lazy"
            onError={() => setImgOk(false)}
            className="w-full h-full object-cover group-hover:scale-[1.04]"
            style={{ transition: "transform 0.6s cubic-bezier(0.22,1,0.36,1)" }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center" style={{ background: "radial-gradient(120% 80% at 30% 20%, rgba(245,197,24,0.20) 0%, rgba(245,197,24,0.05) 45%, transparent 70%), linear-gradient(160deg, #0a1326 0%, #050b14 100%)" }}>
            <div className="font-display text-6xl lg:text-7xl font-extrabold text-[#f5c518]/85" style={{ letterSpacing: "-0.04em" }}>{initials(name)}</div>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#050b14] via-[#050b14]/35 to-transparent" />
        <div className={`absolute top-3 left-3 rounded-full px-2.5 py-1 text-[0.6rem] font-extrabold uppercase tracking-wider ${tag === "Speaker" ? "bg-[#f5c518] text-[#050b14]" : "bg-[#00d1ff] text-[#050b14]"}`}>{tag}</div>
      </div>
      <div className="mt-4">
        <div className="font-display text-lg lg:text-xl font-extrabold text-white leading-tight" style={{ letterSpacing: "-0.025em" }}>{name}</div>
        <div className="text-white/55 text-[0.82rem] mt-1.5 leading-snug">{role}</div>
      </div>
    </motion.div>
  );
};

const Speakers = () => (
  <section id="speakers" className="relative py-24 lg:py-32 bg-[#050b14]" data-testid="speakers-section">
    <div className="absolute inset-0 grain opacity-50" aria-hidden />
    <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={stagger(0.1)} className="max-w-3xl">
        <motion.div variants={fadeUp}>
          <span className="eyebrow"><span className="dot bg-[#f5c518]" />The voices in the room</span>
        </motion.div>
        <motion.h2 variants={fadeUp} className="h-display text-4xl lg:text-6xl mt-6">
          Learn from <span className="accent-gold italic font-light" style={{ fontWeight: 300 }}>industry leaders</span>.
        </motion.h2>
        <motion.p variants={fadeUp} className="text-white/60 text-base lg:text-lg mt-5 leading-relaxed">
          {SPEAKERS.length} leaders confirmed for the 2026 edition — chairmen, CEOs, exchange heads, economists, editors and association leaders steering India's commodity trade, listed in order of seniority.
        </motion.p>
      </motion.div>

      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} variants={stagger(0.06)} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 lg:gap-7 mt-14">
        {SPEAKERS.map((s) => <SpeakerCard key={s.name} {...s} />)}
      </motion.div>

      <motion.p initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-white/45 text-sm text-center mt-12 italic">
        Line-up updated regularly. See who's speaking in which session in the <a href="#agenda" className="text-[#f5c518] not-italic font-semibold hover:underline">full programme ↑</a>.
      </motion.p>
    </div>
  </section>
);

/* ─────────────  Awards  ───────────── */
const Awards = ({ onLeadOpen, onNominate }) => (
  <section id="awards" className="relative py-24 lg:py-32 bg-[#0a1326]">
    <div className="max-w-6xl mx-auto px-5 lg:px-8">
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={stagger(0.1)} className="max-w-3xl">
        <motion.div variants={fadeUp}><span className="eyebrow">The Honours</span></motion.div>
        <motion.h2 variants={fadeUp} className="h-display text-4xl lg:text-6xl mt-6">
          Seventeen honours. <span className="accent-gold italic font-light" style={{ fontWeight: 300 }}>Judged on the work.</span>
        </motion.h2>
        <motion.p variants={fadeUp} className="text-white/70 mt-6 text-[1.05rem] leading-relaxed">
          Across three houses, judged on growth, leadership, innovation and the harder-to-measure business of doing it right. Nominations open now.
        </motion.p>
      </motion.div>

      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} variants={stagger(0.14)} className="grid md:grid-cols-3 gap-6 mt-14 group">
        {Object.entries(AWARDS).map(([k, h]) => (
          <motion.div key={k} variants={fadeUp} className="surface p-7 flex flex-col transition-all duration-500 group-hover:opacity-50 hover:!opacity-100 hover:-translate-y-1.5" data-testid={`award-card-${k}`}>
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center font-display text-2xl text-[#050b14] mb-5 font-extrabold ${
              h.tone === "cyan" ? "bg-[#00d1ff]" : h.tone === "gold" ? "bg-[#f5c518]" : "bg-gradient-to-br from-[#00d1ff] to-[#f5c518]"
            }`}>
              {k}
            </div>
            <h3 className="font-display text-2xl text-white font-bold leading-tight" style={{ letterSpacing: "-0.025em" }}>{h.title}</h3>
            <ul className="mt-5 space-y-2.5 flex-1">
              {h.items.map((it) => (
                <li key={it} className="text-white/70 text-[0.95rem] flex gap-2 group/item">
                  <span className="text-[#f5c518] mt-1">·</span>
                  <span className="flex-1">{it}</span>
                  <button
                    onClick={() => onNominate([it])}
                    className="opacity-0 group-hover/item:opacity-100 transition-opacity text-[0.72rem] text-[#00d1ff] font-bold uppercase tracking-wider whitespace-nowrap ml-2 hover:text-[#f5c518]"
                    data-testid={`nominate-cat-${it.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    Nominate →
                  </button>
                </li>
              ))}
            </ul>
            <button onClick={() => onNominate(h.items)} className="btn btn-cyan w-full mt-6 !py-2.5 text-sm" data-testid={`nominate-house-${k}`}>
              Nominate in this house →
            </button>
          </motion.div>
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7 }}
        className="mt-12 surface p-6 lg:p-7 flex flex-wrap items-center justify-between gap-5"
      >
        <div>
          <div className="font-display text-2xl text-white font-bold" style={{ letterSpacing: "-0.025em" }}>Ready to put a name forward?</div>
          <div className="text-white/60 mt-1 text-[0.95rem]">Nominations are now <span className="text-[#f5c518] font-semibold">COMPLIMENTARY</span> — Gala dinner + jury consideration included, win or not. Nominate for one or more categories at no cost.</div>
        </div>
        <div className="flex gap-3 flex-wrap">
          <MagneticButton as="button" onClick={() => onNominate()} className="btn btn-gold" data-testid="awards-nominate-cta">
            Nominate now →
          </MagneticButton>
          <MagneticButton as="button" onClick={() => onLeadOpen("brochure")} className="btn btn-ghost" data-testid="awards-brochure-cta">
            Download brochure
          </MagneticButton>
        </div>
      </motion.div>
    </div>
  </section>
);

/* ─────────────  Gallery  ───────────── */
const GalleryPhoto = ({ g, i }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [i % 2 === 0 ? "-6%" : "6%", i % 2 === 0 ? "6%" : "-6%"]);
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.7, delay: (i % 4) * 0.06, ease: [0.22, 1, 0.36, 1] }}
      className={`photo-card ${i % 7 === 0 ? "row-span-2 aspect-[3/4] md:aspect-[3/5]" : "aspect-[4/5]"}`}
      data-testid={`gallery-photo-${i + 1}`}
    >
      <motion.img src={g.src} alt={`1st Edition · ${i + 1}`} loading="lazy" style={{ y, height: "112%" }} className="!absolute inset-0" />
    </motion.div>
  );
};

const Gallery = () => (
  <section id="gallery" className="relative py-24 lg:py-32 overflow-hidden">
    <div className="max-w-7xl mx-auto px-5 lg:px-8">
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={stagger(0.1)} className="max-w-3xl">
        <motion.div variants={fadeUp}><span className="eyebrow">1st Edition · Recap</span></motion.div>
        <motion.h2 variants={fadeUp} className="h-display text-4xl lg:text-6xl mt-6">
          The room, last year. <span className="accent-gold italic font-light" style={{ fontWeight: 300 }}>Same energy, bigger this time.</span>
        </motion.h2>
        <motion.p variants={fadeUp} className="text-white/65 mt-5 text-lg max-w-2xl">
          A deliberately mixed, deliberately senior crowd — the whole commodity chain, not one slice of it.
        </motion.p>
      </motion.div>

      <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 gallery-grid" data-testid="gallery-grid">
        {GALLERY.map((g, i) => <GalleryPhoto key={i} g={g} i={i} />)}
      </div>

      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} variants={stagger(0.04)} className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="room-grid">
        {ROOM_TILES.map((t, i) => (
          <motion.div key={t} variants={fadeUp} className="surface px-4 py-3.5 flex items-center gap-3">
            <span className="font-display text-[#00d1ff] text-sm font-bold">{String(i + 1).padStart(2, "0")}</span>
            <span className="text-white/85 text-[0.92rem]">{t}</span>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

/* ─────────────  Checker  ───────────── */
const Checker = ({ onNominate, onRegister }) => {
  const [step, setStep] = useState(0);
  const [picks, setPicks] = useState(Array(QUESTIONS.length).fill(null));
  const [submitting, setSubmitting] = useState(false);
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [submitted, setSubmitted] = useState(false);

  const score = picks.reduce((s, p) => s + (p ?? 0), 0);
  const tier = score >= 7 ? "strong" : score >= 4 ? "close" : "audience";

  const tierContent = {
    strong: { heading: "You're a strong candidate.", body: `Your profile scores ${score}/10 against what the jury looks for. On this, it's worth putting your name forward — the worst case is a great evening; the best case is an honour that travels with your brand.`, cta: "Start your nomination →", action: "nominate", tone: "gold" },
    close: { heading: "You're close. Worth a conversation.", body: `You land at ${score}/10 — a real contender in the right category. Come as a delegate (free seat for the evening sessions, keynote and awards), work the room, and let's talk about whether one of the honours fits your year.`, cta: "Reserve free seat →", action: "register", tone: "cyan" },
    audience: { heading: "Come for the room this year.", body: `At ${score}/10 a nomination might be early — but the evening is exactly where you build toward one. The free delegate pass puts you in front of everyone who matters.`, cta: "Reserve free seat →", action: "register", tone: "cyan" },
  };

  const reset = () => { setStep(0); setPicks(Array(QUESTIONS.length).fill(null)); setSubmitted(false); setContact({ name: "", email: "", phone: "" }); };

  const submitLead = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try { await axios.post(`${API}/leads`, { ...contact, source: "eligibility-checker", score, tier }); setSubmitted(true); }
    catch { setSubmitted(true); }
    finally { setSubmitting(false); }
  };

  const progressPct = step >= QUESTIONS.length ? 100 : (step / QUESTIONS.length) * 100;
  const t = tierContent[tier];

  return (
    <section id="eligibility" className="relative py-24 lg:py-32 overflow-hidden">
      <div className="blob blob-gold w-[420px] h-[420px] -top-32 right-1/4 opacity-30" />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={stagger(0.1)}>
          <motion.div variants={fadeUp}><span className="eyebrow">Free tool · 30 seconds</span></motion.div>
          <motion.h2 variants={fadeUp} className="h-display text-4xl lg:text-6xl mt-6">
            Should you be on stage, or in the <span className="accent-gold italic font-light" style={{ fontWeight: 300 }}>audience</span>?
          </motion.h2>
          <motion.p variants={fadeUp} className="text-white/70 mt-6 text-[1.05rem] leading-relaxed">
            Five quick questions. We'll tell you, honestly, whether your year makes you a strong candidate for a Commodity Market Movers Award — or whether you're better off coming as a delegate this time. No email wall, no sales call.
          </motion.p>
          <motion.ul variants={fadeUp} className="mt-7 space-y-3 text-white/70 text-[0.95rem]">
            {["Live scored against the jury's actual lens", "Instant verdict — nominate, delegate or both", "Used by 200+ commodity leaders last year"].map((x) => (
              <li key={x} className="flex gap-3"><span className="text-[#00d1ff]">▸</span>{x}</li>
            ))}
          </motion.ul>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.8 }} className="relative">
          <div className="absolute -inset-3 bg-gradient-to-br from-[#00d1ff]/25 to-[#f5c518]/25 rounded-[28px] blur-2xl" />
          <div className="relative bg-white text-[#050b14] rounded-[22px] p-7 lg:p-9 shadow-2xl" data-testid="checker-card">
            <div className="flex items-center justify-between text-xs text-[#050b14]/55 uppercase tracking-widest font-bold">
              <span>Eligibility check</span>
              <span>{Math.min(step + 1, QUESTIONS.length)} / {QUESTIONS.length}</span>
            </div>
            <div className="h-1.5 bg-[#e5eaf3] rounded-full mt-3 overflow-hidden">
              <motion.div className="h-full bg-gradient-to-r from-[#008dd2] to-[#f5c518]" initial={false} animate={{ width: `${progressPct}%` }} transition={{ duration: 0.5 }} />
            </div>

            {step < QUESTIONS.length && (
              <AnimatePresence mode="wait">
                <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.35 }} className="mt-7">
                  <div className="font-display text-2xl lg:text-3xl leading-snug font-bold" style={{ letterSpacing: "-0.025em" }}>{QUESTIONS[step].q}</div>
                  {QUESTIONS[step].helper && <div className="text-[#050b14]/55 italic text-sm mt-2">({QUESTIONS[step].helper})</div>}
                  <div className="mt-5 grid sm:grid-cols-2 gap-2.5">
                    {QUESTIONS[step].opts.map(([label, val], optIdx) => {
                      const active = picks[step] === val;
                      return (
                        <button
                          key={label}
                          onClick={() => { const np = [...picks]; np[step] = val; setPicks(np); }}
                          className={`text-left px-4 py-3 rounded-xl border transition-all text-[0.95rem] ${active ? "bg-[#050b14] text-white border-[#050b14]" : "bg-[#f7f9fc] border-[#e0e6ef] hover:border-[#008dd2]"}`}
                          data-testid={`checker-q${step + 1}-opt-${optIdx}`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-7 flex items-center justify-between">
                    <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} className="text-[#050b14]/60 disabled:opacity-30 text-sm font-semibold" data-testid="checker-back">← Back</button>
                    <button onClick={() => setStep((s) => s + 1)} disabled={picks[step] === null} className="btn btn-cyan disabled:opacity-40 disabled:cursor-not-allowed !py-3 !px-6" data-testid="checker-next">
                      {step === QUESTIONS.length - 1 ? "See result" : "Next →"}
                    </button>
                  </div>
                </motion.div>
              </AnimatePresence>
            )}

            {step >= QUESTIONS.length && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-7" data-testid="checker-result">
                <div className="text-xs uppercase tracking-[0.22em] text-[#008dd2] font-bold">Your verdict</div>
                <div className="font-display text-3xl lg:text-4xl leading-tight mt-2 font-extrabold" style={{ letterSpacing: "-0.035em" }}>{t.heading}</div>
                <div className="mt-4 text-[#050b14]/75 leading-relaxed">{t.body}</div>

                {!submitted ? (
                  <form onSubmit={submitLead} className="mt-6 space-y-3" data-testid="checker-lead-form">
                    <div className="text-xs text-[#050b14]/55 font-semibold">Optional — drop your details and we'll send a personalised next step + the brochure.</div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <input required value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} className="field-light" placeholder="Full name" data-testid="checker-input-name" />
                      <input required type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className="field-light" placeholder="Work email" data-testid="checker-input-email" />
                    </div>
                    <input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} className="field-light" placeholder="Phone (optional)" data-testid="checker-input-phone" />
                    <div className="flex flex-col sm:flex-row gap-3 pt-1">
                      <button type="submit" disabled={submitting} className={`btn ${t.tone === "gold" ? "btn-gold" : "btn-cyan"} flex-1`} data-testid="checker-submit-lead">
                        {submitting ? "Sending..." : "Send me the brochure"}
                      </button>
                      {t.action === "nominate" ? (
                        <button type="button" onClick={() => onNominate()} className="btn btn-ghost flex-1 !text-[#050b14] !border-[#050b14]/20 hover:!border-[#050b14]" data-testid="checker-result-cta">
                          {t.cta}
                        </button>
                      ) : (
                        <button type="button" onClick={() => onRegister(contact)} className="btn btn-ghost flex-1 !text-[#050b14] !border-[#050b14]/20 hover:!border-[#050b14]" data-testid="checker-result-cta">
                          {t.cta}
                        </button>
                      )}
                    </div>
                  </form>
                ) : (
                  <div className="mt-6 p-5 rounded-xl bg-[#e8f7ff] border border-[#008dd2]/30" data-testid="checker-success">
                    <div className="font-display text-xl font-bold">On its way.</div>
                    <div className="text-[#050b14]/75 text-[0.95rem] mt-1">We'll be in touch within a working day. Meanwhile —</div>
                    {t.action === "nominate" ? (
                      <button onClick={() => onNominate()} className="btn btn-gold mt-4">{t.cta}</button>
                    ) : (
                      <button onClick={() => onRegister(contact)} className={`btn mt-4 ${t.tone === "gold" ? "btn-gold" : "btn-cyan"}`}>{t.cta}</button>
                    )}
                  </div>
                )}

                <button onClick={reset} className="mt-5 text-sm text-[#050b14]/60 hover:text-[#050b14] underline" data-testid="checker-reset">Run it again</button>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/* ─────────────  Register  ───────────── */
const Register = ({ onNominate, onRegister }) => (
  <section id="register" className="relative py-24 lg:py-32 bg-[#0a1326]">
    <div className="max-w-6xl mx-auto px-5 lg:px-8">
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={stagger(0.1)} className="text-center max-w-2xl mx-auto">
        <motion.div variants={fadeUp}><span className="eyebrow" style={{ justifyContent: "center" }}>Two ways in</span></motion.div>
        <motion.h2 variants={fadeUp} className="h-display text-4xl lg:text-6xl mt-6">
          Pick the door that <span className="accent-gold italic font-light" style={{ fontWeight: 300 }}>fits your year</span>.
        </motion.h2>
      </motion.div>

      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} variants={stagger(0.15)} className="grid md:grid-cols-2 gap-7 mt-14">
        <motion.div variants={fadeUp} className="surface p-8 lg:p-10 relative" data-testid="register-delegate">
          <div className="absolute -top-3 left-8 bg-[#0a8a3a] text-white text-[0.7rem] font-bold uppercase tracking-widest px-3 py-1 rounded-full">Complimentary</div>
          <div className="text-[0.7rem] uppercase tracking-[0.22em] text-[#00d1ff] font-bold">Free Delegate Pass</div>
          <div className="flex items-baseline gap-3 mt-3">
            <span className="font-display text-5xl lg:text-7xl font-extrabold text-[#0a8a3a]" style={{ letterSpacing: "-0.05em" }}>FREE</span>
            <span className="text-white/45 text-sm line-through tabular-nums">₹5,000</span>
          </div>
          <p className="text-white/55 text-xs mt-1">Limited free seats — first come, first served.</p>
          <ul className="mt-6 space-y-3.5 text-white/80 text-[0.97rem]">
            {["3 market sessions + keynote","Seat at the Commodity Market Movers Awards","Senior, cross-sector networking","Confirmation email in under a minute"].map((x) => (
              <li key={x} className="flex gap-3"><span className="text-[#00d1ff] mt-1">✓</span>{x}</li>
            ))}
          </ul>
          <p className="text-white/45 text-xs mt-4 italic">Note — gala dinner is reserved for award nominees & sponsors.</p>
          <MagneticButton as="button" onClick={() => onRegister()} className="btn btn-gold w-full mt-7" data-testid="register-delegate-cta">
            Reserve free seat →
          </MagneticButton>
        </motion.div>

        <motion.div variants={fadeUp} className="surface p-8 lg:p-10 relative !border-[#f5c518]/60" data-testid="register-nomination">
          <div className="absolute -top-3 left-8 bg-[#f5c518] text-[#050b14] text-[0.7rem] font-bold uppercase tracking-widest px-3 py-1 rounded-full">+ Gala dinner</div>
          <div className="text-[0.7rem] uppercase tracking-[0.22em] text-[#f5c518] font-bold">Award Nomination</div>
          <div className="flex items-baseline gap-3 mt-3">
            <span className="font-display text-5xl lg:text-7xl font-extrabold text-[#f5c518]" style={{ letterSpacing: "-0.05em" }}>FREE</span>
            <span className="text-white/45 text-sm line-through tabular-nums">₹25,000</span>
          </div>
          <p className="text-white/55 text-xs mt-1">Complimentary for the 2026 edition — nominate at no cost.</p>
          <ul className="mt-6 space-y-3.5 text-white/80 text-[0.97rem]">
            {[
              "Your nomination put before the jury",
              ["Gala dinner access", "Reserved for nominees & sponsors"],
              "Recognition across event & post-event coverage",
              "Nominate for one or more categories at no cost",
            ].map((x) => (
              <li key={Array.isArray(x) ? x[0] : x} className="flex gap-3">
                <span className="text-[#f5c518] mt-1">★</span>
                {Array.isArray(x) ? (
                  <span><b className="text-white">{x[0]}</b> <span className="text-white/55">— {x[1]}</span></span>
                ) : (
                  <span>{x}</span>
                )}
              </li>
            ))}
          </ul>
          <MagneticButton as="button" onClick={() => onNominate()} className="btn btn-cyan w-full mt-9" data-testid="register-nomination-cta">
            Start free nomination →
          </MagneticButton>
        </motion.div>
      </motion.div>
      <p className="text-center text-white/55 mt-8 text-sm">Group bookings? Sponsorship? Just get in touch.</p>
    </div>
  </section>
);

/* ─────────────  Partner  ───────────── */
const Partner = ({ onLeadOpen }) => (
  <section id="partner" className="relative py-24 lg:py-32">
    <div className="max-w-6xl mx-auto px-5 lg:px-8">
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={stagger(0.1)} className="max-w-3xl">
        <motion.div variants={fadeUp}><span className="eyebrow">Partner with us</span></motion.div>
        <motion.h2 variants={fadeUp} className="h-display text-4xl lg:text-6xl mt-6">
          A small, senior audience is worth more than a <span className="accent-cyan italic font-light" style={{ fontWeight: 300 }}>big anonymous one</span>.
        </motion.h2>
        <motion.p variants={fadeUp} className="text-white/70 mt-6 text-[1.05rem] leading-relaxed">
          If your customers are commodity decision-makers, this is a short evening with a long tail.
        </motion.p>
      </motion.div>

      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} variants={stagger(0.14)} className="grid md:grid-cols-2 gap-7 mt-14">
        {[
          { label: "Sponsorship", blurb: "For trading houses, exchanges, banks, processors, logistics and tech firms who want their name on the evening the market remembers.", points: ["Direct access to senior commodity decision-makers","Brand woven through the awards and dinner","Warm introductions, not cold leads","Visibility across the full outreach campaign","A credible seat in the commodity conversation"], tone: "gold" },
          { label: "Exhibit", blurb: "For solution providers — risk platforms, market intelligence, inspection, financing, infrastructure — who'd rather show than tell.", points: ["Face time with focused, qualified delegates","Demo your capability where buyers can see it","Lead generation built on conversation","Position your brand inside a premium setting","Relationships that outlast the evening"], tone: "cyan" },
        ].map((c) => (
          <motion.div key={c.label} variants={fadeUp} className="surface p-8" data-testid={`partner-${c.label.toLowerCase()}`}>
            <div className={`text-[0.7rem] uppercase tracking-[0.22em] font-bold ${c.tone === "gold" ? "text-[#f5c518]" : "text-[#00d1ff]"}`}>{c.label}</div>
            <p className="text-white/80 mt-3 italic leading-relaxed">{c.blurb}</p>
            <ul className="mt-5 space-y-2.5">
              {c.points.map((p) => (
                <li key={p} className="text-white/70 text-[0.95rem] flex gap-3">
                  <span className={c.tone === "gold" ? "text-[#f5c518]" : "text-[#00d1ff]"}>·</span>{p}
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7 }} className="mt-12 surface p-8 lg:p-10 flex flex-col lg:flex-row items-center justify-between gap-6" data-testid="partner-deck-cta">
        <div className="max-w-2xl">
          <div className="font-display text-2xl lg:text-3xl font-bold" style={{ letterSpacing: "-0.025em" }}>Get the full partner deck.</div>
          <div className="text-white/60 mt-2">Sponsorship slabs, exhibit options, branding inventory, last edition's reach — everything you need to make the case internally.</div>
        </div>
        <MagneticButton as="button" onClick={() => onLeadOpen("sponsorship")} className="btn btn-gold whitespace-nowrap" data-testid="partner-request-deck">
          Request the partner deck →
        </MagneticButton>
      </motion.div>
    </div>
  </section>
);

/* ─────────────  Closing CTA  ───────────── */
const ClosingCTA = ({ onNominate, onRegister }) => (
  <section className="relative py-24 lg:py-32 overflow-hidden">
    <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, #008dd2 0%, #182a6e 45%, #f5c518 130%)" }} />
    <div className="absolute inset-0 grain" />
    <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={stagger(0.1)} className="relative max-w-4xl mx-auto px-5 lg:px-8 text-center">
      <motion.div variants={fadeUp}><span className="eyebrow" style={{ color: "#050b14", justifyContent: "center" }}>1 October · Mumbai</span></motion.div>
      <motion.h2 variants={fadeUp} className="h-display text-5xl lg:text-7xl mt-6 text-white">
        Roundtable. Awards. <span className="italic font-light" style={{ color: "#050b14", fontWeight: 300 }}>You.</span>
      </motion.h2>
      <motion.p variants={fadeUp} className="text-white/90 mt-6 text-lg max-w-xl mx-auto">
        Seats are limited by design. The room only works if you're in it — reserve before the table fills up.
      </motion.p>
      <motion.div variants={fadeUp} className="mt-9 flex flex-wrap gap-3 justify-center">
        <MagneticButton as="button" onClick={() => onRegister()} className="btn btn-white" data-testid="closing-cta-reserve">
          Reserve free seat →
        </MagneticButton>
        <MagneticButton as="button" onClick={onNominate} className="btn btn-gold" data-testid="closing-cta-nominate">
          Nominate now →
        </MagneticButton>
        <a href="#eligibility" className="btn btn-ghost !border-white/40 hover:!border-white">Check eligibility</a>
      </motion.div>
    </motion.div>
  </section>
);

/* ─────────────  FAQ (visible + AIO-friendly)  ───────────── */
const FAQ = () => {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="relative py-24 lg:py-32 bg-[#0a1326]">
      <div className="max-w-5xl mx-auto px-5 lg:px-8">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={stagger(0.1)} className="max-w-3xl">
          <motion.div variants={fadeUp}><span className="eyebrow">Frequently asked</span></motion.div>
          <motion.h2 variants={fadeUp} className="h-display text-4xl lg:text-5xl mt-6">
            Quick answers about <span className="accent-cyan italic font-light" style={{ fontWeight: 300 }}>CMM 2026</span>.
          </motion.h2>
          <motion.p variants={fadeUp} className="text-white/65 mt-5 text-[1.02rem] leading-relaxed">
            Everything the search engines, AI assistants and your CFO want to know — in one place.
          </motion.p>
        </motion.div>

        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} variants={stagger(0.06)} className="mt-12 space-y-3" data-testid="faq-list">
          {FAQ_ITEMS.map((f, i) => {
            const isOpen = open === i;
            return (
              <motion.div key={f.q} variants={fadeUp} className="surface overflow-hidden" data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full text-left px-6 py-5 flex items-center justify-between gap-6 hover:bg-white/[0.02] transition-colors"
                  aria-expanded={isOpen}
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="font-display text-lg lg:text-xl font-bold text-white" style={{ letterSpacing: "-0.02em" }}>{f.q}</span>
                  <span className={`flex-shrink-0 w-8 h-8 rounded-full border border-white/15 flex items-center justify-center transition-transform duration-300 ${isOpen ? "rotate-45 bg-[#00d1ff] border-[#00d1ff] text-[#050b14]" : "text-white/70"}`}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-5 text-white/75 leading-relaxed text-[0.97rem] border-t border-white/8 pt-4">
                        {f.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7 }} className="mt-10 text-center">
          <p className="text-white/55 text-[0.95rem]">Still have a question?</p>
          <a href="mailto:events@teflas.com?subject=CMM%202026%20—%20Question" className="inline-flex items-center gap-2 text-[#00d1ff] font-semibold mt-2 hover:gap-3 transition-all">
            Email events@teflas.com →
          </a>
        </motion.div>
      </div>
    </section>
  );
};

/* ─────────────  Contact  ───────────── */
const Contact = () => (
  <section id="contact" className="relative py-20 lg:py-24 overflow-hidden">
    <div className="blob blob-cyan w-[360px] h-[360px] -right-32 top-1/3 opacity-25" />
    <div className="relative max-w-6xl mx-auto px-5 lg:px-8">
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={stagger(0.1)} className="max-w-3xl">
        <motion.div variants={fadeUp}><span className="eyebrow">Get in touch</span></motion.div>
        <motion.h2 variants={fadeUp} className="h-display text-4xl lg:text-5xl mt-6">
          Questions, table bookings, partnerships — <span className="accent-cyan italic font-light" style={{ fontWeight: 300 }}>just ask</span>.
        </motion.h2>
      </motion.div>

      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} variants={stagger(0.12)} className="mt-12 grid md:grid-cols-3 gap-5" data-testid="contact-grid">
        <motion.div variants={fadeUp} className="surface p-7" data-testid="contact-office">
          <div className="w-11 h-11 rounded-xl bg-[#00d1ff]/12 border border-[#00d1ff]/30 flex items-center justify-center text-[#00d1ff] mb-4">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 2C8 2 5 5 5 9c0 5 7 13 7 13s7-8 7-13c0-4-3-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
          </div>
          <div className="text-[0.7rem] uppercase tracking-[0.22em] text-[#00d1ff] font-bold">Office</div>
          <div className="font-display text-xl font-bold mt-2" style={{ letterSpacing: "-0.025em" }}>Tefla's HQ, Mumbai</div>
          <p className="text-white/65 text-[0.92rem] mt-3 leading-relaxed">
            301 AB &amp; 308, Abhishek Premises,<br />
            C-5 Dalia Industrial Estate,<br />
            Off New Link Road, Andheri (W),<br />
            Mumbai – 400053. India
          </p>
          <a href="https://maps.google.com/?q=Abhishek+Premises+Dalia+Industrial+Estate+Andheri+West+Mumbai" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#00d1ff] text-sm font-semibold mt-4 hover:gap-2 transition-all">
            View on Maps →
          </a>
        </motion.div>

        <motion.div variants={fadeUp} className="surface p-7" data-testid="contact-phone">
          <div className="w-11 h-11 rounded-xl bg-[#f5c518]/12 border border-[#f5c518]/30 flex items-center justify-center text-[#f5c518] mb-4">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z"/></svg>
          </div>
          <div className="text-[0.7rem] uppercase tracking-[0.22em] text-[#f5c518] font-bold">Phone</div>
          <div className="font-display text-xl font-bold mt-2" style={{ letterSpacing: "-0.025em" }}>Talk to the team</div>
          <ul className="mt-3 space-y-1.5">
            <li><a href="tel:+919833855566" className="text-white/85 hover:text-white text-[0.95rem] tabular-nums">+91 98338 55566</a></li>
            <li><a href="tel:+919833755566" className="text-white/85 hover:text-white text-[0.95rem] tabular-nums">+91 98337 55566</a></li>
          </ul>
          <a href="https://wa.me/919833855566?text=Hi%20Tefla's%2C%20I'd%20like%20to%20know%20more%20about%20the%20Commodity%20Market%20Movers%20Awards%202026." target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#f5c518] text-sm font-semibold mt-4 hover:gap-2 transition-all">
            Chat on WhatsApp →
          </a>
        </motion.div>

        <motion.div variants={fadeUp} className="surface p-7" data-testid="contact-email">
          <div className="w-11 h-11 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center text-white mb-4">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><path d="M22 6l-10 7L2 6"/></svg>
          </div>
          <div className="text-[0.7rem] uppercase tracking-[0.22em] text-white/70 font-bold">Email</div>
          <div className="font-display text-xl font-bold mt-2" style={{ letterSpacing: "-0.025em" }}>Drop us a line</div>
          <a href="mailto:events@teflas.com" className="block text-white/85 hover:text-white text-[0.98rem] mt-3 break-all">events@teflas.com</a>
          <p className="text-white/55 text-[0.85rem] mt-2 leading-relaxed">For nominations, sponsorships, table bookings — we reply within a working day.</p>
          <a href="mailto:events@teflas.com?subject=CMM%202026%20—%20Enquiry" className="inline-flex items-center gap-1 text-white text-sm font-semibold mt-4 hover:gap-2 transition-all">
            Start an email →
          </a>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

/* ─────────────  Footer  ───────────── */
const Footer = () => (
  <footer className="bg-[#030711] pt-20 pb-10 border-t border-white/5">
    <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-[1.4fr_1fr_1fr_1fr] gap-10">
      <div>
        <div className="logo-shimmer inline-block">
          <img src="/assets/cmm-logo.svg" alt="CMM" className="h-14 w-auto" style={{ filter: "brightness(0) invert(1)" }} />
        </div>
        <p className="text-white/60 mt-5 text-[0.95rem] leading-relaxed max-w-md">
          Roundtable & Awards 2026 — 2nd Edition. An evening for the people who move India's commodity markets.
        </p>
        <div className="mt-6 flex items-center gap-6">
          <div>
            <div className="text-[0.65rem] uppercase tracking-[0.22em] text-white/40 font-bold">Organised by</div>
            <img src="/assets/teflas.svg" alt="Tefla's" className="h-7 w-auto mt-2" />
          </div>
          <div>
            <div className="text-[0.65rem] uppercase tracking-[0.22em] text-white/40 font-bold">Knowledge Partner</div>
            <img src="/assets/mc3.png" alt="McQube" className="h-7 w-auto mt-2" />
          </div>
        </div>
      </div>
      <div>
        <div className="text-[0.7rem] uppercase tracking-[0.22em] text-[#00d1ff] font-bold">Event</div>
        <ul className="mt-4 space-y-2.5 text-white/65 text-[0.92rem]">
          <li><a href="#experience" className="hover:text-white">The Experience</a></li>
          <li><a href="#agenda" className="hover:text-white">Programme</a></li>
          <li><a href="#awards" className="hover:text-white">Honours</a></li>
          <li><a href="#eligibility" className="hover:text-white">Eligibility</a></li>
          <li><a href="#contact" className="hover:text-white">Contact</a></li>
        </ul>
      </div>
      <div>
        <div className="text-[0.7rem] uppercase tracking-[0.22em] text-[#f5c518] font-bold">Details</div>
        <ul className="mt-4 space-y-2.5 text-white/65 text-[0.92rem]">
          <li>1 October 2026</li>
          <li>The Westin Mumbai Powai Lake</li>
          <li>3:30 PM onwards</li>
          <li><a href="#register" className="text-[#f5c518] hover:underline">Secure pass →</a></li>
        </ul>
      </div>
      <div>
        <div className="text-[0.7rem] uppercase tracking-[0.22em] text-white/55 font-bold">Reach us</div>
        <ul className="mt-4 space-y-2.5 text-white/65 text-[0.92rem]">
          <li><a href="mailto:events@teflas.com" className="hover:text-white">events@teflas.com</a></li>
          <li><a href="tel:+919833855566" className="hover:text-white tabular-nums">+91 98338 55566</a></li>
          <li><a href="tel:+919833755566" className="hover:text-white tabular-nums">+91 98337 55566</a></li>
          <li className="text-white/55 text-[0.85rem] leading-relaxed pt-1">
            301 AB &amp; 308, Abhishek Premises,<br />
            C-5 Dalia Industrial Estate, Off New Link Road,<br />
            Andheri (W), Mumbai – 400053
          </li>
        </ul>
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-5 lg:px-8 mt-14 pt-6 border-t border-white/8 flex flex-col sm:flex-row items-center justify-between gap-3 text-white/40 text-xs">
      <div>© 2026 Commodity Market Movers. Presented by Tefla's.</div>
      <div>Mumbai, India</div>
    </div>
  </footer>
);

/* ─────────────  Nomination Modal (4-step flow — COMPLIMENTARY)  ───────────── */
const NOMINATION_STEPS = ["Categories", "Nominee", "The case", "Review & Confirm"];

const NominationModal = ({ open, onClose, initialCategories = [] }) => {
  const [step, setStep] = useState(0);
  const [categories, setCategories] = useState(initialCategories);
  const [nominee, setNominee] = useState({ name: "", designation: "", company: "", sector: "", years_in_trade: "", email: "", phone: "" });
  const [caseText, setCaseText] = useState("");
  const [links, setLinks] = useState(["", ""]);
  const [nominator, setNominator] = useState({ nominator_name: "", nominator_email: "" });
  const [consent, setConsent] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (open) {
      setStep(0);
      setCategories(initialCategories);
      setNominee({ name: "", designation: "", company: "", sector: "", years_in_trade: "", email: "", phone: "" });
      setCaseText("");
      setLinks(["", ""]);
      setNominator({ nominator_name: "", nominator_email: "" });
      setConsent(true);
      setAiError("");
      setSubmitError("");
    }
  }, [open]); // eslint-disable-line

  if (!open) return null;

  const toggleCategory = (c) => setCategories((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  const polish = async () => {
    setAiLoading(true); setAiError("");
    try {
      const res = await axios.post(`${API}/ai/polish-case`, {
        rough_draft: caseText, categories, nominee_name: nominee.name, company: nominee.company,
      });
      setCaseText(res.data.polished);
    } catch (e) {
      setAiError(e?.response?.data?.detail || "AI assist temporarily unavailable.");
    } finally { setAiLoading(false); }
  };

  const submit = async () => {
    setSubmitting(true); setSubmitError("");
    try {
      const res = await axios.post(`${API}/nominations`, {
        categories,
        nominee,
        case_text: caseText,
        supporting_links: links.filter((l) => l.trim()),
        nominator_name: nominator.nominator_name,
        nominator_email: nominator.nominator_email || null,
        consent,
      });
      // Persist nomination for /thank-you page (fires Meta CompleteRegistration + shows details)
      try {
        sessionStorage.setItem("cmm_last_nomination", JSON.stringify(res.data));
        // Also clear any stale delegate registration so nomination is the source of truth
        sessionStorage.removeItem("cmm_last_registration");
      } catch { /* noop */ }
      // Redirect to dedicated thank-you page
      window.location.assign("/thank-you");
    } catch (e) {
      setSubmitError(e?.response?.data?.detail || "Submission failed. Try again or email aadil@teflas.com.");
      setSubmitting(false);
    }
  };

  const canAdvance = () => {
    if (step === 0) return categories.length >= 1;
    if (step === 1) return nominee.name && nominee.company && nominee.email && nominee.phone;
    if (step === 2) return (caseText || "").trim().length >= 30;
    return true;
  };

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[120] bg-[#030711]/90 backdrop-blur-md flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto" onClick={onClose} data-testid="nomination-modal">
        <motion.div initial={{ y: 30, scale: 0.96 }} animate={{ y: 0, scale: 1 }} exit={{ y: 20, scale: 0.98 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} onClick={(e) => e.stopPropagation()} className="bg-[#fafbfd] text-[#050b14] w-full max-w-3xl rounded-t-[22px] sm:rounded-[22px] shadow-2xl my-0 sm:my-8 relative">
          <button onClick={onClose} className="absolute top-5 right-5 text-[#050b14]/50 hover:text-[#050b14] z-10" aria-label="Close" data-testid="nomination-close">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>

          {/* Header / progress */}
          <div className="px-6 sm:px-9 pt-7 pb-4 border-b border-[#e5eaf3]">
            <div className="flex items-center justify-between">
              <div>
                <div className="inline-flex items-center gap-2 px-2 py-0.5 rounded-full bg-[#e7f8ed] border border-[#0a8a3a]/30 text-[#0a8a3a] text-[0.6rem] font-bold tracking-[0.18em] uppercase">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0a8a3a] animate-pulse" /> Complimentary
                </div>
                <div className="text-[0.68rem] uppercase tracking-[0.22em] text-[#008dd2] font-bold mt-1.5">CMM Awards 2026 · Nominate</div>
                <div className="font-display text-2xl sm:text-[1.7rem] font-extrabold mt-1" style={{ letterSpacing: "-0.03em" }}>
                  {NOMINATION_STEPS[step]}
                </div>
              </div>
              <div className="text-xs font-bold text-[#050b14]/55">Step {step + 1} / 4</div>
            </div>
            <div className="mt-4 flex gap-1.5">
              {NOMINATION_STEPS.map((_, i) => (
                <div key={i} className={`h-1 flex-1 rounded-full transition-colors duration-500 ${i <= step ? "bg-gradient-to-r from-[#008dd2] to-[#f5c518]" : "bg-[#e5eaf3]"}`} />
              ))}
            </div>
          </div>

          <div className="px-6 sm:px-9 py-6 sm:py-7 max-h-[68vh] overflow-y-auto">
            {/* Step 0 — Categories */}
            {step === 0 && (
              <div data-testid="nom-step-categories">
                <p className="text-[#050b14]/70 text-[0.95rem] leading-relaxed">
                  Pick the honour(s) you want to put forward. <span className="font-semibold">Every nomination includes a gala dinner seat and full jury consideration</span> — yours whether it wins or not. Nominate for as many categories as you like — <span className="text-[#0a8a3a] font-semibold">all at no cost</span>.
                </p>
                {Object.entries(AWARDS).map(([k, h]) => (
                  <div key={k} className="mt-6">
                    <div className="flex items-center gap-2.5 mb-3">
                      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg text-white font-display font-extrabold text-sm ${h.tone === "cyan" ? "bg-[#008dd2]" : h.tone === "gold" ? "bg-[#f5c518] !text-[#050b14]" : "bg-gradient-to-br from-[#008dd2] to-[#f5c518]"}`}>{k}</span>
                      <div className="font-display font-bold text-[1.05rem]">{h.title}</div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {h.items.map((it) => {
                        const checked = categories.includes(it);
                        return (
                          <label key={it} className={`cursor-pointer flex items-start gap-3 p-3 rounded-xl border transition-all text-[0.92rem] ${checked ? "border-[#050b14] bg-[#050b14] text-white" : "border-[#e0e6ef] bg-white hover:border-[#008dd2]"}`} data-testid={`nom-cat-${it.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                            <input type="checkbox" className="mt-0.5 accent-[#f5c518]" checked={checked} onChange={() => toggleCategory(it)} />
                            <span className="leading-snug">{it}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Step 1 — Nominee */}
            {step === 1 && (
              <div data-testid="nom-step-nominee">
                <p className="text-[#050b14]/70 text-[0.95rem]">Who's being nominated? (You can nominate yourself — most people do.)</p>
                <div className="grid sm:grid-cols-2 gap-3 mt-5">
                  <input className="field-light" placeholder="Full name *" value={nominee.name} onChange={(e) => setNominee({ ...nominee, name: e.target.value })} data-testid="nom-input-name" />
                  <input className="field-light" placeholder="Designation" value={nominee.designation} onChange={(e) => setNominee({ ...nominee, designation: e.target.value })} data-testid="nom-input-designation" />
                  <input className="field-light" placeholder="Company *" value={nominee.company} onChange={(e) => setNominee({ ...nominee, company: e.target.value })} data-testid="nom-input-company" />
                  <input className="field-light" placeholder="Sector (Agri / Metals / Energy ...)" value={nominee.sector} onChange={(e) => setNominee({ ...nominee, sector: e.target.value })} data-testid="nom-input-sector" />
                  <input className="field-light" placeholder="Years in trade" value={nominee.years_in_trade} onChange={(e) => setNominee({ ...nominee, years_in_trade: e.target.value })} data-testid="nom-input-years" />
                  <input type="email" className="field-light" placeholder="Work email *" value={nominee.email} onChange={(e) => setNominee({ ...nominee, email: e.target.value })} data-testid="nom-input-email" />
                  <input className="field-light sm:col-span-2" placeholder="Phone *" value={nominee.phone} onChange={(e) => setNominee({ ...nominee, phone: e.target.value })} data-testid="nom-input-phone" />
                </div>
                <div className="mt-5 p-4 rounded-xl bg-[#e8f7ff] border border-[#008dd2]/30 text-[0.88rem] text-[#050b14]/75">
                  <b>Nominating someone else?</b> Optional — add your name and email so we can keep you in the loop.
                  <div className="grid sm:grid-cols-2 gap-3 mt-3">
                    <input className="field-light !bg-white" placeholder="Your name" value={nominator.nominator_name} onChange={(e) => setNominator({ ...nominator, nominator_name: e.target.value })} data-testid="nom-input-nominator-name" />
                    <input type="email" className="field-light !bg-white" placeholder="Your email" value={nominator.nominator_email} onChange={(e) => setNominator({ ...nominator, nominator_email: e.target.value })} data-testid="nom-input-nominator-email" />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2 — The case */}
            {step === 2 && (
              <div data-testid="nom-step-case">
                <p className="text-[#050b14]/70 text-[0.95rem]">
                  Tell the jury why this nomination deserves a win. Numbers, milestones, market-firsts. Aim for 200–300 words —
                  <span className="font-semibold text-[#050b14]"> or paste rough notes and let our AI polish them.</span>
                </p>
                <div className="mt-4 relative">
                  <textarea
                    rows={9}
                    className="field-light leading-relaxed"
                    placeholder="e.g. led 40% YoY growth in 2025, opened three new West-Africa corridors, won ISO sustainability cert, scaled team 12→38, revenue ₹80cr → ₹220cr..."
                    value={caseText}
                    onChange={(e) => setCaseText(e.target.value)}
                    data-testid="nom-input-case"
                  />
                  <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
                    <div className="text-xs text-[#050b14]/55">{(caseText || "").trim().split(/\s+/).filter(Boolean).length} words</div>
                    <button
                      onClick={polish}
                      disabled={aiLoading || (caseText || "").trim().length < 10}
                      className="btn btn-cyan !py-2 !px-4 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="nom-ai-polish"
                    >
                      {aiLoading ? "Polishing…" : "Polish with AI"}
                    </button>
                  </div>
                </div>
                {aiError && <div className="mt-2 text-xs text-red-600">{aiError}</div>}
                <div className="mt-6">
                  <div className="text-[0.7rem] uppercase tracking-[0.18em] font-bold text-[#050b14]/55">Supporting links (optional)</div>
                  <div className="grid sm:grid-cols-2 gap-3 mt-2">
                    {links.map((l, i) => (
                      <input key={i} className="field-light" placeholder={i === 0 ? "Press / LinkedIn URL" : "Annual report / case study URL"} value={l} onChange={(e) => { const n = [...links]; n[i] = e.target.value; setLinks(n); }} data-testid={`nom-input-link-${i}`} />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3 — Review & Confirm (FREE) */}
            {step === 3 && (
              <div data-testid="nom-step-review">
                <div className="rounded-2xl border border-[#e0e6ef] p-5 bg-white">
                  <div className="text-[0.7rem] uppercase tracking-[0.18em] font-bold text-[#008dd2]">Nominee</div>
                  <div className="font-display font-bold text-xl mt-1" style={{ letterSpacing: "-0.025em" }}>{nominee.name}</div>
                  <div className="text-[#050b14]/65 text-sm">{nominee.designation ? `${nominee.designation} · ` : ""}{nominee.company}</div>
                  <div className="text-[#050b14]/55 text-xs mt-1">{nominee.email} · {nominee.phone}</div>
                </div>

                <div className="mt-4 rounded-2xl border border-[#e0e6ef] p-5 bg-white">
                  <div className="text-[0.7rem] uppercase tracking-[0.18em] font-bold text-[#008dd2]">Categories ({categories.length})</div>
                  <ul className="mt-2 space-y-1.5">
                    {categories.map((c) => (
                      <li key={c} className="flex items-start gap-2 text-[0.92rem]"><span className="text-[#f5c518] mt-0.5">★</span>{c}</li>
                    ))}
                  </ul>
                </div>

                <div className="mt-4 rounded-2xl border-2 border-[#0a8a3a]/40 p-5 bg-gradient-to-br from-[#e7f8ed] to-white">
                  <div className="inline-flex items-center gap-2 px-2 py-0.5 rounded-full bg-[#0a8a3a] text-white text-[0.6rem] font-bold tracking-[0.18em] uppercase">
                    Complimentary · 2026 Edition
                  </div>
                  <div className="font-display font-extrabold text-2xl mt-2" style={{ letterSpacing: "-0.025em" }}>Your nomination is on the house.</div>
                  <ul className="mt-3 space-y-1.5 text-[0.92rem] text-[#050b14]/75">
                    <li className="flex gap-2"><span className="text-[#0a8a3a] font-bold">✓</span>Jury review across every selected category</li>
                    <li className="flex gap-2"><span className="text-[#0a8a3a] font-bold">✓</span>Seat at the gala dinner — win or not</li>
                    <li className="flex gap-2"><span className="text-[#0a8a3a] font-bold">✓</span>Coverage across event & post-event media</li>
                    <li className="flex gap-2"><span className="text-[#0a8a3a] font-bold">✓</span>No card required · Confirmation email in minutes</li>
                  </ul>
                </div>

                <label className="flex items-start gap-2 mt-4 text-[0.85rem] text-[#050b14]/70 cursor-pointer">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 accent-[#f5c518]" data-testid="nom-consent" />
                  <span>I confirm the facts above are accurate, and I'm okay with Tefla's reaching out for any clarification before the jury reviews this nomination.</span>
                </label>
                {submitError && <div className="mt-2 text-sm text-red-600" data-testid="nom-submit-error">{submitError}</div>}
              </div>
            )}
          </div>

          {/* Footer / actions */}
          <div className="px-6 sm:px-9 py-4 border-t border-[#e5eaf3] bg-white rounded-b-[22px] flex items-center justify-between gap-3">
            <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} className="text-[#050b14]/60 disabled:opacity-30 text-sm font-semibold" data-testid="nom-back">← Back</button>
            <div className="text-xs text-[#050b14]/55 hidden sm:block">
              {categories.length > 0 && step < 3 && (
                <>{categories.length} categor{categories.length === 1 ? "y" : "ies"} · <b className="text-[#0a8a3a]">FREE</b></>
              )}
            </div>
            {step < 3 ? (
              <button onClick={() => setStep((s) => s + 1)} disabled={!canAdvance()} className="btn btn-cyan disabled:opacity-40 disabled:cursor-not-allowed !py-2.5 !px-5 text-sm" data-testid="nom-next">
                Continue →
              </button>
            ) : (
              <button onClick={submit} disabled={submitting || !consent} className="btn btn-gold disabled:opacity-40 disabled:cursor-not-allowed !py-2.5 !px-5 text-sm" data-testid="nom-submit">
                {submitting ? "Submitting..." : "Submit nomination →"}
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/* ─────────────  Register Modal — FREE delegate pass (no payment)  ───────────── */
const getUTM = () => {
  if (typeof window === "undefined") return {};
  try {
    const p = new URLSearchParams(window.location.search);
    const u = {};
    ["source", "medium", "campaign", "content", "term", "fbclid", "gclid"].forEach((k) => {
      const v = p.get(`utm_${k}`) || (k === "fbclid" || k === "gclid" ? p.get(k) : null);
      if (v) u[k] = v;
    });
    return u;
  } catch { return {}; }
};

const RegisterModal = ({ open, onClose, prefill = {}, source = "register" }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", designation: "" });
  const [state, setState] = useState("idle"); // idle | sending | done | error
  const [error, setError] = useState("");
  const [reg, setReg] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({ name: prefill.name || "", email: prefill.email || "", phone: prefill.phone || "", company: prefill.company || "", designation: prefill.designation || "" });
      setState("idle"); setError(""); setReg(null);
    }
  }, [open]); // eslint-disable-line

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!form.name || !form.email || !form.phone) { setError("Name, email and phone are required."); return; }
    setState("sending");
    try {
      const res = await axios.post(`${API}/registrations`, { ...form, source, utm: getUTM() });
      // Persist the just-completed registration so the /thank-you page can render details + fire Pixel
      try { sessionStorage.setItem("cmm_last_registration", JSON.stringify(res.data)); } catch { /* noop */ }
      setReg(res.data);
      setState("done");
      // Redirect to dedicated thank-you page — fires fbq('CompleteRegistration', {content_name:'CMM 2026'}) on mount
      window.location.assign("/thank-you");
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Could not register — try again or email events@commoditymarketmovers.com.");
      setState("error");
    }
  };

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[110] bg-[#030711]/90 backdrop-blur-md flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto" onClick={onClose} data-testid="register-modal">
        <motion.div initial={{ y: 30, scale: 0.96 }} animate={{ y: 0, scale: 1 }} exit={{ y: 20, scale: 0.98 }} transition={{ duration: 0.35 }} onClick={(e) => e.stopPropagation()} className="bg-white text-[#050b14] w-full max-w-lg rounded-t-[22px] sm:rounded-[22px] shadow-2xl my-0 sm:my-8 p-7 lg:p-8 relative">
          <button onClick={onClose} className="absolute top-5 right-5 text-[#050b14]/50 hover:text-[#050b14] z-10" aria-label="Close" data-testid="register-modal-close">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>

          {state !== "done" ? (
            <>
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#e7f8ed] border border-[#0a8a3a]/30 text-[#0a8a3a] text-[0.65rem] font-bold tracking-[0.18em] uppercase">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0a8a3a] animate-pulse" /> Free · Complimentary
              </div>
              <div className="font-display text-3xl lg:text-4xl mt-3 leading-tight font-extrabold" style={{ letterSpacing: "-0.035em" }}>
                Reserve your <span className="italic font-light text-[#008dd2]" style={{ fontWeight: 300 }}>free seat</span>.
              </div>
              <p className="text-[#050b14]/65 mt-3 text-[0.95rem] leading-relaxed">
                1 October 2026 · The Westin Mumbai Powai Lake. Five business sessions, the keynote, the awards ceremony and networking — on the house.
              </p>
              <ul className="mt-4 grid grid-cols-2 gap-x-3 gap-y-1.5 text-[0.86rem] text-[#050b14]/75">
                {["3 market sessions", "Keynote address", "Awards ceremony", "Senior networking"].map((x) => (
                  <li key={x} className="flex gap-2"><span className="text-[#0a8a3a] font-bold">✓</span>{x}</li>
                ))}
              </ul>
              <p className="text-[#050b14]/50 text-xs mt-3 italic">Note — gala dinner is reserved for award nominees & sponsors.</p>

              <form onSubmit={submit} className="mt-5 space-y-3" data-testid="register-form">
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="field-light" placeholder="Full name *" data-testid="register-name" />
                <div className="grid sm:grid-cols-2 gap-3">
                  <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="field-light" placeholder="Work email *" data-testid="register-email" />
                  <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="field-light" placeholder="Phone *" data-testid="register-phone" />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="field-light" placeholder="Company" data-testid="register-company" />
                  <input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} className="field-light" placeholder="Designation" data-testid="register-designation" />
                </div>
                {error && <div className="text-red-600 text-sm" data-testid="register-error">{error}</div>}
                <button type="submit" disabled={state === "sending"} className="btn btn-gold w-full mt-2 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="register-submit">
                  {state === "sending" ? "Reserving your seat…" : "Confirm my free seat →"}
                </button>
                <p className="text-[#050b14]/50 text-xs text-center pt-1">Confirmation email arrives in under a minute. We never spam.</p>
              </form>
            </>
          ) : (
            <div data-testid="register-success">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#e7f8ed] border border-[#0a8a3a]/30 text-[#0a8a3a] text-[0.65rem] font-bold tracking-[0.18em] uppercase">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0a8a3a]" /> Seat reserved
              </div>
              <div className="font-display text-3xl lg:text-4xl mt-3 leading-tight font-extrabold text-[#0a8a3a]" style={{ letterSpacing: "-0.035em" }}>
                See you on 1 October.
              </div>
              <p className="text-[#050b14]/70 mt-3 text-[0.95rem] leading-relaxed">
                <b>{reg?.name}</b>, your seat is locked in. A confirmation email is on its way to <b>{reg?.email}</b> — keep that handy at the entry desk.
              </p>
              <div className="mt-3 p-3 rounded-xl bg-[#f7f9fc] border border-[#e0e6ef] text-[0.85rem] text-[#050b14]/75">
                <div className="flex justify-between gap-3"><span>Reference</span><span className="font-mono text-[#050b14]">#{(reg?.id || "").slice(0, 8).toUpperCase()}</span></div>
                <div className="flex justify-between gap-3 mt-1"><span>Date</span><span className="text-[#050b14]">1 October 2026 · 3:30 PM</span></div>
                <div className="flex justify-between gap-3 mt-1"><span>Venue</span><span className="text-[#050b14]">The Westin Mumbai Powai Lake</span></div>
              </div>
              <div className="mt-5 p-4 rounded-xl bg-[#fff8dc] border-l-4 border-[#f5c518]">
                <div className="text-[0.7rem] uppercase tracking-[0.18em] font-bold text-[#050b14]/55">Want the gala dinner too?</div>
                <p className="text-[0.92rem] mt-1">Every award nomination includes the <b>gala dinner + jury consideration</b> — win or not.</p>
                <button onClick={() => { onClose(); setTimeout(() => window.__openNomination?.(), 250); }} className="btn btn-gold !py-2 !px-4 text-xs mt-3" data-testid="register-success-nominate">
                  See the 17 honours →
                </button>
              </div>
              <button onClick={onClose} className="btn btn-cyan w-full mt-5" data-testid="register-done">Done</button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/* ─────────────  Lead Modal  ───────────── */
const LeadModal = ({ open, asset, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", designation: "" });
  const [state, setState] = useState("idle");

  useEffect(() => {
    if (open) { setForm({ name: "", email: "", phone: "", company: "", designation: "" }); setState("idle"); }
  }, [open]);

  if (!open) return null;
  const isSponsor = asset === "sponsorship";

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      const res = await axios.post(`${API}/leads`, { ...form, source: asset });
      const leadId = res?.data?.id || Date.now();
      const [first, ...rest] = String(form.name || "").trim().split(/\s+/);
      trackConversion("Lead", {
        event_id: `lead_${leadId}`,
        custom_data: {
          content_name: isSponsor ? "Sponsor Enquiry" : "Brochure / Contact",
          content_category: asset,
        },
        user_data: {
          email: form.email,
          phone: form.phone,
          first_name: first || "",
          last_name: rest.join(" "),
        },
      });
      setState("done");
    } catch {
      setState("error");
    }
  };

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] bg-[#030711]/85 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose} data-testid="lead-modal">
        <motion.div initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }} transition={{ duration: 0.35 }} onClick={(e) => e.stopPropagation()} className="bg-white text-[#050b14] rounded-[22px] w-full max-w-lg p-7 lg:p-9 relative shadow-2xl">
          <button onClick={onClose} className="absolute top-5 right-5 text-[#050b14]/50 hover:text-[#050b14]" aria-label="Close" data-testid="lead-modal-close">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
          <div className="text-[0.7rem] uppercase tracking-[0.22em] text-[#008dd2] font-bold">
            {isSponsor ? "Partner Deck · Free" : "Event Brochure · Free"}
          </div>
          <div className="font-display text-3xl lg:text-4xl mt-2 leading-tight font-extrabold" style={{ letterSpacing: "-0.035em" }}>
            {isSponsor
              ? <>The 14-page <span className="italic font-light text-[#f5c518]" style={{ fontWeight: 300 }}>partner deck</span>.</>
              : <>The 16-page <span className="italic font-light text-[#008dd2]" style={{ fontWeight: 300 }}>event brochure</span>.</>}
          </div>
          <p className="text-[#050b14]/65 mt-3 text-[0.95rem]">
            {isSponsor
              ? "Sponsorship slabs, exhibit options, branding inventory, audience composition, last edition's reach — everything you need to make the internal case."
              : "The full picture of CMM Roundtable & Awards 2026 — drop your details and download instantly."}
          </p>
          {!isSponsor && (
            <ul className="mt-4 grid sm:grid-cols-2 gap-x-4 gap-y-1.5 text-[0.86rem] text-[#050b14]/75">
              {[
                "All 17 award categories + criteria",
                "Full evening programme + speakers",
                "Who attends · seniority profile",
                "Last edition's recap & numbers",
                "Nomination & delegate pricing",
                "How to take part",
              ].map((x) => (
                <li key={x} className="flex gap-2"><span className="text-[#008dd2] font-bold">▸</span>{x}</li>
              ))}
            </ul>
          )}

          {state !== "done" ? (
            <form onSubmit={submit} className="mt-6 space-y-3" data-testid="lead-modal-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="field-light" placeholder="Full name" data-testid="lead-name" />
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="field-light" placeholder="Work email" data-testid="lead-email" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="field-light" placeholder="Phone" data-testid="lead-phone" />
                <input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} className="field-light" placeholder="Designation" data-testid="lead-designation" />
              </div>
              <input required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="field-light" placeholder="Company" data-testid="lead-company" />
              {state === "error" && <div className="text-red-600 text-sm">Something went wrong. Please email aadil@teflas.com.</div>}
              <button disabled={state === "sending"} className={`btn ${isSponsor ? "btn-gold" : "btn-cyan"} w-full mt-2`} data-testid="lead-submit">
                {state === "sending" ? "Sending..." : isSponsor ? "Send me the deck →" : "Email + download brochure →"}
              </button>
              <p className="text-[#050b14]/50 text-xs text-center pt-1">One email, no spam, no sequence. Promise.</p>
            </form>
          ) : (
            <div className="mt-7 p-5 rounded-xl bg-[#e8f7ff] border border-[#008dd2]/30" data-testid="lead-success">
              <div className="font-display text-2xl font-bold">It's yours.</div>
              <div className="text-[#050b14]/70 mt-2 text-[0.95rem]">
                We've also emailed the {isSponsor ? "partner deck" : "brochure"} to <strong>{form.email}</strong>. Tap below to download it right now.
              </div>
              <a href={isSponsor ? BROCHURE_URL : BROCHURE_URL} download target="_blank" rel="noreferrer" className={`btn ${isSponsor ? "btn-gold" : "btn-cyan"} w-full mt-5`} data-testid="lead-download-now">
                Download the {isSponsor ? "deck" : "brochure"} (PDF, ~4 MB) →
              </a>
              <div className="text-center text-[#050b14]/50 text-xs mt-3">Didn't get the email in 2 min? Check spam, or ping <a className="text-[#008dd2] underline" href="mailto:aadil@teflas.com">aadil@teflas.com</a>.</div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/* ─────────────  Exit Intent  ───────────── */
const ExitIntent = ({ onOpen }) => {
  const [shown, setShown] = useState(false);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (sessionStorage.getItem("cmm_exit_shown")) return;
    const handler = (e) => {
      if (e.clientY < 6 && !shown) { setShown(true); setVisible(true); sessionStorage.setItem("cmm_exit_shown", "1"); }
    };
    document.addEventListener("mouseleave", handler);
    return () => document.removeEventListener("mouseleave", handler);
  }, [shown]);

  if (!visible) return null;
  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[110] bg-[#030711]/90 backdrop-blur-md flex items-center justify-center p-4" data-testid="exit-intent">
        <motion.div initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} className="bg-white text-[#050b14] rounded-[22px] max-w-md w-full p-8 relative shadow-2xl">
          <button onClick={() => setVisible(false)} className="absolute top-4 right-4 text-[#050b14]/50" aria-label="Close" data-testid="exit-intent-close">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
          <div className="text-[0.7rem] uppercase tracking-[0.22em] text-[#f5c518] font-bold">Before you go</div>
          <div className="font-display text-3xl mt-2 leading-tight font-extrabold" style={{ letterSpacing: "-0.035em" }}>Take the <span className="italic font-light text-[#008dd2]" style={{ fontWeight: 300 }}>brochure</span> with you.</div>
          <p className="text-[#050b14]/65 mt-3">Agenda, categories, last year's recap — one PDF, sent across instantly.</p>
          <button onClick={() => { setVisible(false); onOpen("brochure"); }} className="btn btn-gold w-full mt-6" data-testid="exit-intent-cta">Send me the brochure →</button>
          <button onClick={() => setVisible(false)} className="block mx-auto mt-3 text-[#050b14]/45 text-sm underline">No thanks</button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/* ─────────────  Sticky Bar — full gold ribbon (Meta-ad conversion)  ───────────── */
const StickyBar = ({ onNominate, onRegister }) => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const on = () => setShow(window.scrollY > 800);
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-0 left-0 right-0 z-[80] bg-[#f5c518] text-[#050b14] shadow-[0_-12px_40px_-8px_rgba(245,197,24,0.45)]"
          style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0px)" }}
          data-testid="sticky-bar"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-3.5 flex items-center justify-between gap-3">
            <div className="hidden sm:flex items-center gap-3 min-w-0">
              <svg className="shrink-0" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M3 11l18-5v12l-18-5z" /><path d="M11 14v5l3 1v-5" /></svg>
              <div className="leading-tight min-w-0">
                <div className="font-extrabold uppercase tracking-wider text-[0.78rem] sm:text-sm truncate" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>Complimentary Passes · Limited availability</div>
                <div className="text-[0.7rem] text-[#050b14]/70 truncate">Don't miss out — 1 October 2026, Mumbai.</div>
              </div>
            </div>
            <div className="sm:hidden font-extrabold uppercase tracking-wider text-[0.78rem] leading-tight" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
              FREE · Limited seats
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={onNominate} className="hidden md:inline-flex items-center px-4 py-2 rounded-full bg-[#050b14]/10 hover:bg-[#050b14]/20 text-[#050b14] text-xs font-bold uppercase tracking-wider" data-testid="sticky-nominate">Nominate</button>
              <button onClick={() => onRegister?.()} className="inline-flex items-center px-4 sm:px-5 py-2.5 rounded-full bg-[#050b14] text-[#f5c518] text-xs sm:text-sm font-extrabold uppercase tracking-wider hover:bg-[#0a1326] whitespace-nowrap" data-testid="sticky-reserve">Claim free pass →</button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ─────────────  WhatsApp  ───────────── */
const WhatsAppFloat = () => (
  <a href="https://wa.me/919833855566?text=Hi%20Tefla's%2C%20I'd%20like%20to%20know%20more%20about%20the%20Commodity%20Market%20Movers%20Awards%202026." target="_blank" rel="noreferrer" className="wa-float" aria-label="Chat on WhatsApp" data-testid="whatsapp-float">
    <svg width="28" height="28" viewBox="0 0 32 32" fill="currentColor"><path d="M16 .4C7.4.4.4 7.4.4 16c0 2.8.7 5.5 2.1 7.9L.2 31.6l7.9-2.2c2.3 1.3 4.9 2 7.6 2H16c8.6 0 15.6-7 15.6-15.6S24.6.4 16 .4zm0 28.5c-2.4 0-4.7-.6-6.7-1.8l-.5-.3-4.7 1.3 1.3-4.6-.3-.5C3.8 20.9 3 18.5 3 16 3 8.8 8.8 3 16 3s13 5.8 13 13-5.8 12.9-13 12.9zm7.2-9.7c-.4-.2-2.3-1.1-2.7-1.2-.4-.1-.6-.2-.9.2-.3.4-1 1.2-1.2 1.4-.2.2-.4.3-.8.1-.4-.2-1.7-.6-3.2-2-1.2-1.1-2-2.4-2.2-2.8-.2-.4 0-.6.2-.8.2-.2.4-.5.6-.7.2-.2.3-.4.4-.7.1-.3 0-.5 0-.7-.1-.2-.9-2.1-1.2-2.9-.3-.8-.6-.6-.9-.7H10c-.3 0-.7.1-1 .5s-1.3 1.3-1.3 3.1c0 1.8 1.3 3.6 1.5 3.9.2.3 2.6 4 6.4 5.6.9.4 1.6.6 2.1.8.9.3 1.7.2 2.4.1.7-.1 2.3-.9 2.6-1.8.3-.9.3-1.6.2-1.8-.1-.2-.4-.3-.7-.5z"/></svg>
  </a>
);

/* ─────────────  SEO  ───────────── */
const useSEO = () => {
  useEffect(() => {
    const SITE_URL = "https://commoditymarketmovers.com";
    const TITLE = "Commodity Market Movers Roundtable & Awards 2026 · Mumbai, 1 October | India's Commodity Industry Event";
    const DESC = "India's premier annual evening for senior commodity-trade leaders. 17 award categories across agri, metals, bullion, energy & supply chain. 1 October 2026, Mumbai. FREE delegate pass for the evening sessions & awards. Award nomination ₹25,000/category (incl. gala dinner + jury consideration). Convened by Tefla's.";

    document.title = TITLE;

    const setMeta = (selector, attrs, content) => {
      let m = document.querySelector(selector);
      if (!m) {
        m = document.createElement("meta");
        Object.entries(attrs).forEach(([k, v]) => m.setAttribute(k, v));
        document.head.appendChild(m);
      }
      m.setAttribute("content", content);
    };
    const name = (n, c) => setMeta(`meta[name="${n}"]`, { name: n }, c);
    const prop = (p, c) => setMeta(`meta[property="${p}"]`, { property: p }, c);

    name("description", DESC);
    name("keywords", [
      "commodity awards India 2026", "commodity market summit Mumbai",
      "commodity industry conference India", "commodity trade event Mumbai",
      "commodity market movers awards", "CMM Awards 2026",
      "agri commodity awards India", "edible oil trade awards",
      "bullion and precious metals awards", "metals and mining awards India",
      "commodity broking awards", "commodity logistics conference",
      "supply chain awards India", "Tefla's commodity event",
      "commodity nominations 2026", "Indian commodity leadership awards",
    ].join(", "));
    name("author", "Tefla's");
    name("robots", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
    name("theme-color", "#050b14");

    let canon = document.querySelector("link[rel='canonical']");
    if (!canon) { canon = document.createElement("link"); canon.rel = "canonical"; document.head.appendChild(canon); }
    canon.href = SITE_URL + "/";

    const ogImage = `${SITE_URL}/assets/og-share.jpg`;
    prop("og:type", "website");
    prop("og:site_name", "Commodity Market Movers");
    prop("og:title", "Commodity Market Movers · Roundtable & Awards 2026, Mumbai");
    prop("og:description", "An invite-led evening for India's senior commodity-trade leaders. 17 honours across agri, metals, energy & supply chain. 1 October 2026, Mumbai.");
    prop("og:url", SITE_URL + "/");
    prop("og:image", ogImage);
    prop("og:image:width", "1200");
    prop("og:image:height", "630");
    prop("og:image:alt", "Commodity Market Movers Roundtable & Awards 2026 — Mumbai, 1 October");
    prop("og:locale", "en_IN");

    name("twitter:card", "summary_large_image");
    name("twitter:title", "Commodity Market Movers · Roundtable & Awards 2026, Mumbai");
    name("twitter:description", "1 October 2026, Mumbai. The annual room that moves India's commodity markets.");
    name("twitter:image", ogImage);

    let fav = document.querySelector("link[rel='icon']");
    if (!fav) { fav = document.createElement("link"); fav.rel = "icon"; document.head.appendChild(fav); }
    fav.type = "image/png";
    fav.href = "/assets/favicon.png";

    const setLd = (id, data) => {
      let s = document.getElementById(id);
      if (!s) { s = document.createElement("script"); s.type = "application/ld+json"; s.id = id; document.head.appendChild(s); }
      s.textContent = JSON.stringify(data);
    };

    setLd("ld-event", {
      "@context": "https://schema.org",
      "@type": "BusinessEvent",
      name: "Commodity Market Movers Roundtable & Awards 2026 (2nd Edition)",
      alternateName: ["CMM 2026", "Commodity Market Movers Awards 2026", "Commodity Market Movers Roundtable 2026"],
      description: DESC,
      startDate: "2026-10-01T15:30:00+05:30",
      endDate: "2026-10-01T23:00:00+05:30",
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      eventStatus: "https://schema.org/EventScheduled",
      image: [ogImage, `${SITE_URL}/assets/hero-1st-edition.jpg`],
      url: SITE_URL + "/",
      inLanguage: "en-IN",
      maximumAttendeeCapacity: 200,
      audience: { "@type": "BusinessAudience", audienceType: "Senior leaders, traders, refiners, financiers, policymakers in India's commodity industry" },
      keywords: "commodity awards India, commodity market summit Mumbai, commodity industry conference 2026, agri commodity awards, bullion awards, metals awards, energy commodity awards",
      location: {
        "@type": "Place",
        name: "The Westin Mumbai Powai Lake",
        address: { "@type": "PostalAddress", streetAddress: "The Westin Mumbai Powai Lake, Off Jogeshwari–Vikhroli Link Road", addressLocality: "Mumbai", addressRegion: "Maharashtra", postalCode: "400087", addressCountry: "IN" },
      },
      offers: [
        { "@type": "Offer", name: "Free Delegate Pass", price: "0", priceCurrency: "INR", availability: "https://schema.org/InStock", validFrom: "2026-01-01T00:00:00+05:30", url: SITE_URL + "/#register" },
        { "@type": "Offer", name: "Complimentary Award Nomination", price: "0", priceCurrency: "INR", availability: "https://schema.org/InStock", validFrom: "2026-01-01T00:00:00+05:30", url: SITE_URL + "/#register" },
      ],
      organizer: { "@type": "Organization", name: "Tefla's", url: SITE_URL + "/" },
      sponsor: { "@type": "Organization", name: "McQube" },
    });

    setLd("ld-org", {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Tefla's",
      url: SITE_URL + "/",
      logo: `${SITE_URL}/assets/teflas.svg`,
      email: "events@teflas.com",
      telephone: ["+91-98338-55566", "+91-98337-55566"],
      address: { "@type": "PostalAddress", streetAddress: "301 AB & 308, Abhishek Premises, C-5 Dalia Industrial Estate, Off New Link Road, Andheri (W)", addressLocality: "Mumbai", addressRegion: "Maharashtra", postalCode: "400053", addressCountry: "IN" },
    });

    setLd("ld-breadcrumb", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL + "/" },
        { "@type": "ListItem", position: 2, name: "Honours", item: SITE_URL + "/#awards" },
        { "@type": "ListItem", position: 3, name: "Register", item: SITE_URL + "/#register" },
      ],
    });

    setLd("ld-website", {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Commodity Market Movers Roundtable & Awards 2026",
      alternateName: "CMM Awards 2026",
      url: SITE_URL + "/",
      inLanguage: "en-IN",
      publisher: { "@type": "Organization", name: "Tefla's", url: SITE_URL + "/" },
    });

    // 17 award categories as an ItemList — surfaces well in AI Overviews & Perplexity
    const ALL_HONOURS = [
      "Agri Commodity Leader of the Year",
      "Excellence in Edible Oil Trade",
      "Grain & Feed Leadership",
      "Sugar & Bioenergy Excellence",
      "Emerging Agri Trade Enterprise",
      "Sustainability Leadership in Agri",
      "Commodity Market Leader of the Year",
      "Precious Metals & Bullion Excellence",
      "Metals & Mining Leadership",
      "Energy & Industrial Commodity Excellence",
      "Commodity Innovation & Technology",
      "Lifetime Contribution to Commodity Trade",
      "Young Commodity Leader",
      "Women Leadership in Commodities",
      "Global Trade Excellence",
      "Supply Chain & Logistics Leadership",
      "Excellence in Risk & Market Intelligence",
    ];
    setLd("ld-honours", {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "CMM Awards 2026 — 17 honour categories",
      description: "All 17 award categories at the Commodity Market Movers Roundtable & Awards 2026, across Agri, Non-Agri and Special Recognition houses.",
      numberOfItems: ALL_HONOURS.length,
      itemListElement: ALL_HONOURS.map((name, i) => ({
        "@type": "ListItem", position: i + 1, name, url: SITE_URL + "/#awards",
      })),
    });
  }, []);
};

// Fires ViewContent (browser Pixel only — no PII needed) once per section as it
// enters the viewport. Uses IntersectionObserver + a sessionStorage-backed guard
// so refreshes don't re-fire the same section.
const useViewContentTracking = () => {
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const targets = [
      { id: "speakers", name: "Speakers" },
      { id: "agenda", name: "Programme" },
      { id: "awards", name: "Honours (17 categories)" },
      { id: "gallery", name: "Last Edition Recap" },
      { id: "partner", name: "Partner / Sponsorship" },
      { id: "register", name: "Register / Nominate" },
    ];
    const fired = new Set();
    let SS;
    try {
      SS = JSON.parse(sessionStorage.getItem("cmm_vc_fired") || "[]");
      SS.forEach((n) => fired.add(n));
    } catch {
      SS = [];
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const meta = targets.find((t) => t.id === entry.target.id);
          if (!meta || fired.has(meta.name)) return;
          fired.add(meta.name);
          mpTrack("ViewContent", { content_name: `CMM 2026 - ${meta.name}` });
          try { sessionStorage.setItem("cmm_vc_fired", JSON.stringify([...fired])); } catch { /* noop */ }
        });
      },
      { rootMargin: "0px 0px -30% 0px", threshold: 0.15 }
    );
    targets.forEach((t) => {
      const el = document.getElementById(t.id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, []);
};

/* ─────────────  Home  ───────────── */
const Home = () => {
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadAsset, setLeadAsset] = useState("brochure");
  const [nomOpen, setNomOpen] = useState(false);
  const [nomInitialCats, setNomInitialCats] = useState([]);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [registerPrefill, setRegisterPrefill] = useState({});
  const [registerSource, setRegisterSource] = useState("register");
  useSEO();
  useViewContentTracking();
  useEffect(() => { axios.get(`${API}/`).catch(() => {}); }, []);

  const openLead = (asset) => { setLeadAsset(asset); setLeadOpen(true); };
  const openNomination = (cats = []) => { setNomInitialCats(cats); setNomOpen(true); };
  const openRegister = (prefill = {}, source = "register") => {
    setRegisterPrefill(prefill || {});
    setRegisterSource(source || "register");
    setRegisterOpen(true);
  };

  // expose globally for any deep-linked button
  useEffect(() => {
    window.__openNomination = openNomination;
    window.__openRegister = openRegister;
  }, []);

  return (
    <div className="relative">
      {/* SR-only SEO H1 — full keyword-rich event name for crawlers & screen-readers */}
      <h1 className="sr-only">Commodity Market Movers Roundtable & Awards 2026 — India's premier commodity industry event in Mumbai, 1 October 2026. Free delegate registration. Awards, roundtable and networking for senior commodity-trade leaders. Convened by Tefla's.</h1>
      <ScrollProgress />
      <CustomCursor />
      <Nav onLeadOpen={openLead} onNominate={() => openNomination()} onRegister={() => openRegister({}, "nav")} />
      <Hero onNominate={() => openNomination()} onRegister={() => openRegister({}, "hero")} />
      <MarqueeStrip />
      <WhyAttend onRegister={() => openRegister({}, "why-attend")} />
      <Speakers />
      <TwoHalves onNominate={() => openNomination()} />
      <Evening />
      <Agenda />
      <Awards onLeadOpen={openLead} onNominate={openNomination} />
      <Gallery />
      <Checker onNominate={() => openNomination()} onRegister={(prefill) => openRegister(prefill, "checker")} />
      <Register onNominate={() => openNomination()} onRegister={() => openRegister({}, "register-section")} />
      <Partner onLeadOpen={openLead} />
      <ClosingCTA onNominate={() => openNomination()} onRegister={() => openRegister({}, "closing-cta")} />
      <Contact />
      <Footer />
      <StickyBar onNominate={() => openNomination()} onRegister={() => openRegister({}, "sticky-bar")} />
      <WhatsAppFloat />
      <ExitIntent onOpen={openLead} />
      <LeadModal open={leadOpen} asset={leadAsset} onClose={() => setLeadOpen(false)} />
      <NominationModal open={nomOpen} onClose={() => setNomOpen(false)} initialCategories={nomInitialCats} />
      <RegisterModal open={registerOpen} onClose={() => setRegisterOpen(false)} prefill={registerPrefill} source={registerSource} />
    </div>
  );
};

function App() {
  if (typeof window !== "undefined") {
    const path = window.location.pathname.replace(/\/$/, "");
    if (path === "/admin") return <AdminPage />;
    if (path === "/thank-you" || path === "/thankyou") return <ThankYouPage />;
  }
  return <Home />;
}

export default App;
